import { isChangeRequestId } from '../components/CustomerOrderDetails/changeRequestUtils';
import { isLegacyOrder } from '../components/CustomerOrderList/cocoOrderUtils.js';
import { isSfdcOrder } from '../components/CustomerOrderList/customerOrderUtils';
import { toKauppaSlug } from '../components/DeviceDetailsCoCo/deviceDetailsUtilsCoCo.js';
import { createDnsRecordsLoader } from '../components/DnsManagement/dnsManagementUtils.js';
import { InvoiceSearchParams } from '../components/Invoices/Invoices.js';
import {
  mobileM2MSubscriptionCards4G,
  mobileM2MSubscriptionCards5G,
} from '../components/OrderSubscriptionSelection/content/LaitenettiSubscriptionCardContent';
import { combineAddOnRulesResponses } from '../components/SubscriptionDetails/addOnDependencyUtils';
import { SUBSCRIPTION_TYPE_SEARCH_FILTER_PARAM } from '../components/Subscriptions/SubscriptionsVoice.js';
import { TableSortOrder, TableUrlParams, getItemsPerPageFromOptionsOrDefault } from '../components/Table';
import { CustomerOrderStatus } from '../generated/api/customerOrderStatus.js';
import { OnlineModelCategory } from '../generated/api/onlineModelCategory.js';
import { QuoteStatusType } from '../generated/api/quoteStatusType';
import { SourceSystem } from '../generated/api/sourceSystem.js';
import { SubscriptionAction } from '../generated/api/subscriptionAction';
import { SubscriptionType } from '../generated/api/subscriptionType.js';
import { getSubscriptionTypes } from '../public/common/util/category.js';
import { isElisaIdV3Enabled } from '../public/site/authUtils';
import { createPreLoadedAddOnRules } from '../public/site/path/FixedBroadbandOrder/fixedBroadbandAddOnUtil.js';
import {
  CocoOrderUrlParams,
  CustomerOrderUrlParams,
} from '../public/site/path/SelfService/OrderHistory/customerOrderConstants.js';
import {
  getActiveAccountMasterId,
  getInvoicePaymentMdmId,
  getPrimaryMdmId,
  getShoppingCart,
} from '../selfservice/common/localStorageUtils.js';
import { getOnlineModelFilteringOutNonOfferAddOns, mergeArrays } from '../selfservice/reducers/reducerUtils.js';
import { deviceName } from './constants/pathInterfaces';
import {
  isEoe,
  isEoeFinishedReviewPath,
  isEoeOwnRedeemRequestsPath,
  isEoePendingReviewPath,
  paths,
} from './constants/pathVariables.js';
import {
  AddOnRulesSubscriptionType,
  CustomerOrderListFields,
  DeviceVariantProductTypeCoCo,
  ModelType,
  OfferType,
  ShoppingBasketCoCoScope,
  SubscriptionCategory,
} from './enums.js';
import {
  fetchAddOnRules,
  fetchAllSecondaryCompanyInfos,
  fetchBillChannels,
  fetchBillingAccount,
  fetchBillingAccounts,
  fetchBillingAccountsCoco,
  fetchChangeRequestDetails,
  fetchCocoOrder,
  fetchCocoOrders,
  fetchCompanyInfo,
  fetchCompanyInfoAndEnabledOnlineModels,
  fetchContact,
  fetchContactInvitation,
  fetchContacts,
  fetchContactsBillingAccounts,
  fetchCustomer,
  fetchCustomerOrderAdditionalInfo,
  fetchCustomerOrders,
  fetchDeviceOffersForOfferType,
  fetchDeviceVariants,
  fetchDnsRecordHistory,
  fetchDnsRecords,
  fetchDnsRecordsHistory,
  fetchEmployeeProductList,
  fetchEmployeeProductLists,
  fetchEmployeeProductSelection,
  fetchEmployeeProductSelections,
  fetchHolidays,
  fetchInvitation,
  fetchInvitations,
  fetchInvoice,
  fetchInvoiceDocuments,
  fetchInvoices,
  fetchMfaDetails,
  fetchMobileIdContract,
  fetchMobileIdContracts,
  fetchMobileIdContractsByMdmId,
  fetchNavigationLoader,
  fetchNumberRange,
  fetchOnboardingRequests,
  fetchOnlineModelForCode,
  fetchOnlineModelHeaders,
  fetchOnlineModels,
  fetchOpenSupportCases,
  fetchProductOfferByOfferId,
  fetchProductOffersByOfferId,
  fetchProductOffersForOfferType,
  fetchProposalByProposalId,
  fetchProposals,
  fetchPublicPageLoader,
  fetchReports,
  fetchSecondaryAccounts,
  fetchServiceAgreements,
  fetchSubscription,
  fetchSubscriptionActions,
  fetchSubscriptionAggregates,
  fetchSubscriptionScheduledChanges,
  fetchSubscriptions,
  fetchSupportCase,
  fetchSupportCaseHistory,
  fetchSupportCases,
  fetchUserCustomerOrders,
  fetchV3PrivateOnlineModels,
  fetchVaihtoetuItemGroups,
  fetchVirtualCatalog,
  fetchVirtualCatalogs,
  getRelatedCompanyHierarchyDetails,
  hasPendingActionsForContact,
  hasPendingSubscriptionActions,
  updateProposalByProposalId,
} from './fetch.js';
import {
  getShoppingBasketFromLocalStorage as getShoppingBasketFromLocalStorageCoCo,
  parseBasketJson as parseBasketJsonCoCo,
} from './hooks/useShoppingBasketCoCo.js';
import { collectProductCategoriesOfVirtualCatalog } from './utils/catalogUtils';
import { getContactSort, sortContactsByFirstName } from './utils/contactUtils.js';
import { DEVICE_LIST_TAB_PARAM, DeviceListTabCoCo, getSelectedDeviceListTab } from './utils/deviceListTabsUtilsCoCo.js';
import { DEVICE_VARIANT_CATEGORIES, toDeviceVariantCategoryQuery } from './utils/deviceVariantCategoryUtilsCoCo.js';
import { convertToCommonErrors } from './utils/errorUtils.js';
import { replacePipeWithCommaInQueryParams } from './utils/filterUtils.js';
import { mergeObjects } from './utils/objectUtils.js';
import { resolveSort } from './utils/supportCaseUtils.js';
import type { SubscriptionsQuery } from './fetch.js';
import type { DefaultListSearchParams } from '../components/Table';
import type { AddOnRule } from './types/addOnRule.js';
import type { CommonError } from './types/errors.js';
import type { DeviceVariantGroupsByTypeCoCo } from './utils/deviceSelectionDeviceUtilsCoCo.js';
import type { SubscriptionsDeviceMultiFilter } from '../components/SubscriptionLists/SubscriptionTable';
import type { AddOnRulesResponse } from '../generated/api/addOnRulesResponse.js';
import type { BillChannel } from '../generated/api/billChannel.js';
import type { BillingAccount } from '../generated/api/billingAccount.js';
import type { BillingAccountCoco } from '../generated/api/billingAccountCoco.js';
import type { BillingAccountSearchResponse } from '../generated/api/billingAccountSearchResponse.js';
import type { BillingAccountsResponse } from '../generated/api/billingAccountsResponse.js';
import type { CatalogSearchResponse } from '../generated/api/catalogSearchResponse';
import type { ChangeRequestDetails } from '../generated/api/changeRequestDetails';
import type { CocoProductOrderDetail } from '../generated/api/cocoProductOrderDetail.js';
import type { CompanyInfoResponse } from '../generated/api/companyInfoResponse.js';
import type { Contact } from '../generated/api/contact.js';
import type { ContactInvitation } from '../generated/api/contactInvitation';
import type { ContactsResponse } from '../generated/api/contactsResponse.js';
import type { Contract } from '../generated/api/contract';
import type { CustomerOrder } from '../generated/api/customerOrder.js';
import type { CustomerOrderAdditionalInfo } from '../generated/api/customerOrderAdditionalInfo.js';
import type { CustomerResponse } from '../generated/api/customerResponse.js';
import type { EmployeeProductListResponse } from '../generated/api/employeeProductListResponse.js';
import type { EmployeeProductSelectionResponse } from '../generated/api/employeeProductSelectionResponse.js';
import type { GetServiceAgreementsResponse } from '../generated/api/getServiceAgreementsResponse';
import type { GetSubscriptionScheduledChanges } from '../generated/api/getSubscriptionScheduledChanges.js';
import type { Invitation } from '../generated/api/invitation';
import type { InvitationLanding } from '../generated/api/invitationLanding';
import type { Invoice } from '../generated/api/invoice.js';
import type { InvoiceDocumentsResponse } from '../generated/api/invoiceDocumentsResponse.js';
import type { InvoicesResponse } from '../generated/api/invoicesResponse.js';
import type { MfaDetailsResponse } from '../generated/api/mfaDetailsResponse';
import type { MoveContactPendingActionsResponse } from '../generated/api/moveContactPendingActionsResponse';
import type { NavigationResponse } from '../generated/api/navigationResponse';
import type { NumberRangeResponse } from '../generated/api/numberRangeResponse';
import type { OnboardingRequestsResponse } from '../generated/api/onboardingRequestsResponse.js';
import type { OnlineModel } from '../generated/api/onlineModel.js';
import type { OnlineModelHeader } from '../generated/api/onlineModelHeader.js';
import type { PageResponse } from '../generated/api/pageResponse';
import type { ProductOfferingsResponse } from '../generated/api/productOfferingsResponse';
import type { ProposalResponse } from '../generated/api/proposalResponse';
import type { ReportItemResponse } from '../generated/api/reportItemResponse';
import type { SecondaryAccount } from '../generated/api/secondaryAccount';
import type { Subscription } from '../generated/api/subscription.js';
import type { SubscriptionActionsResponse } from '../generated/api/subscriptionActionsResponse.js';
import type { SubscriptionAggregationsResponse } from '../generated/api/subscriptionAggregationsResponse.js';
import type { SubscriptionDetailsSelectedAddOns } from '../generated/api/subscriptionDetailsSelectedAddOns';
import type { SubscriptionSearchResponse } from '../generated/api/subscriptionSearchResponse.js';
import type { SubscriptionsResponse } from '../generated/api/subscriptionsResponse.js';
import type { SupportCaseDataBundle } from '../generated/api/supportCaseDataBundle.js';
import type { SupportCaseHeader } from '../generated/api/supportCaseHeader.js';
import type { SupportCaseHistory } from '../generated/api/supportCaseHistory.js';
import type { SupportCasesResponse } from '../generated/api/supportCasesResponse.js';
import type { SupportCasesSearchResponse } from '../generated/api/supportCasesSearchResponse.js';
import type { SortableLimitableQuery } from '../generated/api/uiApiMethods';
import type { UpdateProposalRequest } from '../generated/api/updateProposalRequest';
import type { UpdateProposalResponse } from '../generated/api/updateProposalResponse';
import type { VaihtoetuItemGroupList1 } from '../generated/api/vaihtoetuItemGroupList1';
import type { VirtualCatalog } from '../generated/api/virtualCatalog';
import type { CartItemType } from '../selfservice/common/shopping-cart/shoppingCartInterfaces.js';
import type { LoaderFunctionArgs } from 'react-router';

export interface SubscriptionIdParams {
  subscriptionId: string;
}

export type SubscriptionLoaderArgs = LoaderFunctionArgs & { params: SubscriptionIdParams };

export interface SupportCaseLoaderResponse {
  supportCase: SupportCaseDataBundle;
  history: SupportCaseHistory[];
}

export interface BillingAccountLoaderResponse {
  billChannels: BillChannel[];
  contacts: Contact[];
}

export interface InvoiceLoaderResponse {
  invoice: Invoice;
  billChannels: BillChannel[];
  openSupportCases: SupportCaseHeader[];
  billingAccount?: BillingAccount;
}

export interface DeviceSubscriptionLoaderResponse {
  billingAccounts: BillingAccountsResponse;
  companyInfo: CompanyInfoResponse;
  contacts: Contact[];
  hasPendingActions: boolean;
  subscription: Subscription;
  hasPendingAdminReview: boolean;
  billChannels: BillChannel[];
  subscriptionScheduledChanges?: GetSubscriptionScheduledChanges;
  billingAccountRecordType?: string;
}

export type DeviceSubPostChangeRequestLoaderResponse = Pick<
  DeviceSubscriptionLoaderResponse,
  'companyInfo' | 'hasPendingActions' | 'subscription'
>;

export interface SubscriptionAddOnLoaderResponse {
  hasPendingActions: boolean;
  subscription: Subscription;
  addOn: SubscriptionDetailsSelectedAddOns;
}

const defaultRequest = {
  offset: 0,
  order: 'desc',
};

type LoaderDefaultParams = DefaultListSearchParams & {
  params: URLSearchParams;
  itemsPerPage?: number;
  mdmId?: string;
};

const requireMdmIdFromRequest = (request: Request): string =>
  new URL(request.url).searchParams.get('mdmId') ||
  (() => {
    throw new Error('missing mdmId');
  })();

const getMdmIdFromRequest = (request: Request): string | undefined =>
  new URL(request.url).searchParams.get('mdmId') || undefined;

export const getSearchParams = (request: Request): LoaderDefaultParams => {
  const params = new URLSearchParams(new URL(request.url).search);
  const itemsPerPage = getItemsPerPageFromOptionsOrDefault(params.get('limit') || undefined);
  const offset = params.get('offset') || '0';
  const order = params.get('order') || undefined;
  const search = params.get('search') || undefined;
  const sort = params.get('sort') || undefined;
  return { itemsPerPage, offset, order, search, sort, params };
};

const getFilterInvoicesSearchParams = (request: Request) => {
  const params = new URLSearchParams(new URL(request.url).search);
  const paidInvoicesOnly = Boolean(params.get(InvoiceSearchParams.paidInvoicesOnly)) || undefined;
  const openInvoicesOnly = Boolean(params.get(InvoiceSearchParams.openInvoicesOnly)) || undefined;
  return { paidInvoicesOnly, openInvoicesOnly };
};

const buildSubscriptionsQuery = (
  request: Request,
  category?: SubscriptionCategory,
  subscriptionTypes?: SubscriptionType[],
  subscriptionContactId?: string,
  deviceFilters?: SubscriptionsDeviceMultiFilter
): SubscriptionsQuery => {
  const { params, offset, itemsPerPage, ...rest } = getSearchParams(request);
  const subscriptionType =
    subscriptionTypes ||
    (params.get(SUBSCRIPTION_TYPE_SEARCH_FILTER_PARAM) as SubscriptionType | null) ||
    (category && getSubscriptionTypes(category)) ||
    undefined;
  const subscriptionSubType = params.get('subscriptionSubType');
  return mergeObjects(defaultRequest, {
    details: true,
    offset: Number(offset),
    limit: itemsPerPage,
    subscriptionType,
    subscriptionSubType,
    subscriptionContactId,
    useSearchService: true,
    ...replacePipeWithCommaInQueryParams(Object.fromEntries(params), ['limit']),
    ...deviceFilters,
    ...rest,
  });
};

const buildSubscriptionsQueryByBillingAccountDisplayId = (
  request: Request,
  billingAccountDisplayId: string,
  categories: SubscriptionCategory[]
): SubscriptionsQuery => {
  const { params, offset, itemsPerPage, ...rest } = getSearchParams(request);
  const subscriptionType = categories.flatMap(cat => getSubscriptionTypes(cat));
  return mergeObjects(defaultRequest, {
    details: true,
    offset: Number(offset),
    limit: itemsPerPage,
    subscriptionType,
    billingAccountDisplayId,
    useSearchService: true,
    ...replacePipeWithCommaInQueryParams(Object.fromEntries(params), ['limit']),
    ...rest,
  });
};

export interface PersonBillingAccountLoaderData {
  billingAccounts: BillingAccountsResponse;
}

export const personBillingAccountLoader = async (): Promise<PersonBillingAccountLoaderData> => ({
  billingAccounts: await fetchBillingAccounts({ useSearchService: true }),
});

export const supportCaseLoader = async ({
  params,
  request,
}: LoaderFunctionArgs): Promise<SupportCaseLoaderResponse> => {
  if (params.supportCaseDisplayId === undefined) {
    throw new Error('missing supportCaseDisplayId');
  }
  const mdmId = requireMdmIdFromRequest(request);
  return Promise.all([
    fetchSupportCase(params.supportCaseDisplayId, mdmId),
    fetchSupportCaseHistory(params.supportCaseDisplayId, mdmId),
  ]).then(fetchResponse => ({ supportCase: fetchResponse[0], history: fetchResponse[1].caseHistory || [] }));
};

export const supportCasesLoader = ({ request }: LoaderFunctionArgs) => {
  const { params, sort, offset, itemsPerPage, ...rest } = getSearchParams(request);
  const mdmId = getMdmIdFromRequest(request);
  return fetchSupportCases(
    mergeObjects(defaultRequest, {
      feature: params.get('feature'),
      status: params.get('status'),
      offset: Number(offset),
      limit: itemsPerPage,
      sort: resolveSort(sort),
      ...rest,
    }),
    mdmId
  );
};

export const orderConfirmationsLoader = ({ request }: LoaderFunctionArgs) => {
  const { sort, offset, itemsPerPage, ...rest } = getSearchParams(request);
  const mdmId = getMdmIdFromRequest(request);
  return fetchCustomerOrders(
    mergeObjects(defaultRequest, {
      useSearchService: true,
      offset: Number(offset),
      limit: itemsPerPage,
      status: CustomerOrderStatus.COMPLETED,
      sort: sort || CustomerOrderListFields.CREATED,
      excludeChangeRequests: true,
      onlySfdcOrders: true,
      ...rest,
    }),
    false,
    mdmId
  );
};

export const billingAccountLayoutLoader = async ({ params, request }: LoaderFunctionArgs): Promise<BillingAccount> => {
  if (params.billingAccountId === undefined) {
    throw new Error('missing billingAccountId');
  }
  const mdmId = requireMdmIdFromRequest(request);
  const billingAccountsResponse = await fetchBillingAccount(params.billingAccountId, mdmId);
  const billingAccount = billingAccountsResponse.billingAccounts?.[0] as BillingAccount;
  if (!billingAccount) {
    throw new Response('Billing account not found', { status: 404 });
  }

  return billingAccount;
};

export const billingAccountLoader = async ({ request }: LoaderFunctionArgs): Promise<BillingAccountLoaderResponse> => {
  const mdmId = requireMdmIdFromRequest(request);
  const [billChannels, contactsResponse] = await Promise.all([
    fetchBillChannels(mdmId),
    fetchContacts({ offset: 0 }, { mdmId }),
  ]);
  return {
    billChannels,
    contacts: contactsResponse.contacts || [],
  };
};

export const billingAccountSubscriptionsLoader = async ({
  params,
  request,
}: LoaderFunctionArgs): Promise<SubscriptionsResponse> => {
  if (params.billingAccountId === undefined) {
    throw new Error('missing billingAccountId');
  }
  const mdmId = requireMdmIdFromRequest(request);
  const query = buildSubscriptionsQueryByBillingAccountDisplayId(
    request,
    params.billingAccountId,
    Object.values(SubscriptionCategory)
  );
  return await fetchSubscriptions(query, mdmId);
};

export interface BillingAccountInvoicesData {
  invoices: InvoicesResponse;
}

export const billingAccountInvoicesLoader = async ({
  request,
  params,
}: LoaderFunctionArgs): Promise<BillingAccountInvoicesData> => {
  const { itemsPerPage, offset = 0, search, sort = 'due', order = 'desc' } = getSearchParams(request);
  const mdmId = requireMdmIdFromRequest(request);
  const { paidInvoicesOnly, openInvoicesOnly } = getFilterInvoicesSearchParams(request);

  return {
    invoices: await fetchInvoices(
      {
        useSearchService: true,
        search: `${params.billingAccountId}${search ? ' ' + search : ''}`,
        sort,
        order,
        limit: itemsPerPage,
        offset: Number(offset),
        paidInvoicesOnly,
        openInvoicesOnly,
      },
      mdmId
    ),
  };
};

export const billingAccountInvoiceDocumentsLoader = async ({ request, params }: LoaderFunctionArgs) => {
  const { itemsPerPage, offset = 0, search, sort = 'created', order = 'desc' } = getSearchParams(request);
  const mdmId = requireMdmIdFromRequest(request);
  return {
    documents: await fetchInvoiceDocuments(
      {
        useSearchService: true,
        search: `${params.billingAccountId}${search ? ' ' + search : ''}`,
        sort,
        order,
        limit: itemsPerPage,
        offset: Number(offset),
      },
      mdmId
    ),
  };
};

export interface BillingAccountListLoaderData {
  billingAccounts?: BillingAccountsResponse;
}

/**
 * Get billing accounts from Elasticsearch so that they can be shown in the
 * billing account list.
 *
 * For the billing account list billing accounts from all source systems (SFDC,
 * MIPA, TELLUS) should be shown without restrictions. Restrictions may apply
 * to billing accounts shown in dropdowns when they are selected for orders
 * and such.
 */
export const billingAccountListLoader = async ({ request }: LoaderFunctionArgs) => {
  const { itemsPerPage, offset, search, sort, order } = getSearchParams(request);
  const mdmId = getMdmIdFromRequest(request);
  return {
    billingAccounts: await fetchBillingAccounts(
      {
        limit: itemsPerPage,
        offset: Number(offset),
        search,
        sort,
        order,
        useSearchService: true,
      },
      { mdmId }
    ),
  };
};

/**
 * Loader for Catalog Configurations including billing-accounts, bill-channels and contacts
 * billing-accounts and bill-contacts calls are fast, so they're required from the start
 * contacts are slow, so the data is referred and used later in the application for optimal UX
 */
export const catalogConfigurationsLoader = async ({ request }: LoaderFunctionArgs) => {
  const mdmId = requireMdmIdFromRequest(request);
  return {
    billingAccounts: await fetchBillingAccounts({ useSearchService: true, sourceSystem: SourceSystem.SFDC }, { mdmId }),
    billChannels: await fetchBillChannels(mdmId),
    contacts: await fetchContacts({ offset: 0 }, { mdmId }),
    companyInfo: await fetchCompanyInfo(mdmId),
  };
};

export interface BaLoaderData {
  billChannels: BillChannel[];
  contacts: ContactsResponse;
  companyInfo: CompanyInfoResponse;
}

export const createNewBillingAccountLoader = async ({ request }: LoaderFunctionArgs): Promise<BaLoaderData> => {
  const mdmId = requireMdmIdFromRequest(request);
  return {
    billChannels: await fetchBillChannels(mdmId),
    contacts: await fetchContacts({ offset: 0 }, { mdmId }),
    companyInfo: await fetchCompanyInfo(mdmId),
  };
};

export interface PlaceOrderLoaderData extends BaLoaderData {
  mdmId: string;
  expiringEppSubscriptions: Subscription[];
  holidays: Date[];
  addOnRules?: AddOnRulesResponse;
}

export const placeOrderLoader = async (): Promise<PlaceOrderLoaderData> => {
  const mdmId = getActiveAccountMasterId();
  if (!mdmId) {
    // Return stub data when mdmId is unavailable (e.g. server-side rendering or before auth completes).
    // PlaceOrderPath will redirect based on auth/basket state before this data is used.
    return {
      billChannels: [],
      contacts: {} as ContactsResponse,
      companyInfo: {} as CompanyInfoResponse,
      mdmId: '',
      expiringEppSubscriptions: [],
      holidays: [],
    };
  }
  const [billChannels, contacts, companyInfo, deviceSubscriptionsResponse, holidaysResponse, addOnRules] =
    await Promise.all([
      fetchBillChannels(mdmId),
      fetchContacts({ offset: 0 }, { mdmId }),
      fetchCompanyInfo(mdmId),
      fetchSubscriptions(
        { subscriptionType: [SubscriptionType.DEVICE], details: true, expiringOrExpired: true, eppOnly: true },
        mdmId
      ),
      fetchHolidays(),
      fetchAddOnRules(),
    ]);
  const holidays: Date[] = holidaysResponse.dates.map(dateNumber => new Date(dateNumber));
  return {
    billChannels,
    contacts,
    companyInfo,
    mdmId,
    expiringEppSubscriptions: deviceSubscriptionsResponse.subscriptions ?? [],
    holidays,
    addOnRules,
  } satisfies PlaceOrderLoaderData;
};

export const customerOrdersLoader = ({ request }: LoaderFunctionArgs) => {
  const { params, sort, offset, itemsPerPage, ...rest } = getSearchParams(request);
  const mdmId = getMdmIdFromRequest(request);
  const path = new URL(request.url).pathname;
  const allStatuses = Object.values(CustomerOrderStatus);
  const notPendingStatuses = allStatuses.filter(e => e !== CustomerOrderStatus.PENDING_APPROVAL);
  const overrideStatus1 = isEoePendingReviewPath(path) ? CustomerOrderStatus.PENDING_APPROVAL : undefined;
  const overrideStatus2 = isEoeFinishedReviewPath(path) ? notPendingStatuses.join('|') : undefined;
  return fetchCustomerOrders(
    mergeObjects(defaultRequest, {
      useSearchService: true,
      offset: Number(offset),
      limit: itemsPerPage,
      status: overrideStatus1 ?? overrideStatus2 ?? params.get(TableUrlParams.STATUS) ?? undefined,
      sort: sort || CustomerOrderListFields.CREATED,
      customerOrderType: params.get(CustomerOrderUrlParams.CUSTOMER_ORDER_TYPE) ?? undefined,
      productType: params.get(CustomerOrderUrlParams.PRODUCT_TYPE) ?? undefined,
      ...rest,
    }),
    isEoe(path),
    mdmId
  );
};

export const cocoCustomerOrdersLoader = async ({ request }: LoaderFunctionArgs) => {
  const legacyOrders = await customerOrdersLoader({ request } as LoaderFunctionArgs);
  const { params, sort, offset, itemsPerPage, search } = getSearchParams(request);
  const mdmId = getMdmIdFromRequest(request);
  const cocoOrders = await fetchCocoOrders(
    {
      offset: Number(offset),
      limit: itemsPerPage,
      sort: sort || CustomerOrderListFields.CREATED,
      search,
      state: params.get(CocoOrderUrlParams.ORDER_STATUS) ?? undefined,
      productType: params.get(CocoOrderUrlParams.PRODUCT_TYPE) ?? undefined,
      orderType: params.get(CocoOrderUrlParams.ORDER_TYPE) ?? undefined,
      dateFrom: params.get(CocoOrderUrlParams.ORDER_DATE_FROM) ?? undefined,
      dateTo: params.get(CocoOrderUrlParams.ORDER_DATE_TO) ?? undefined,
    },
    mdmId
  );
  return { legacyOrders, cocoOrders };
};

export const userCustomerOrdersLoader = ({ request }: LoaderFunctionArgs) => {
  const { params, sort, offset, itemsPerPage, ...rest } = getSearchParams(request);
  return fetchUserCustomerOrders(
    mergeObjects(defaultRequest, {
      useSearchService: true,
      offset: Number(offset),
      limit: itemsPerPage,
      status: params.get('status') || undefined,
      sort: sort || CustomerOrderListFields.CREATED,
      ...rest,
    })
  );
};

export interface UserCustomerOrderLoaderData {
  customerOrder: CustomerOrder;
  companyInfo: CompanyInfoResponse;
}

export const userCustomerOrderLoader = async ({ params }: LoaderFunctionArgs): Promise<UserCustomerOrderLoaderData> => {
  if (params.orderId === undefined) {
    throw new Error('missing customerOrderId');
  }
  const customerOrder = (await fetchUserCustomerOrders({ customerOrderDisplayId: params.orderId })).customerOrders?.[0];
  if (!customerOrder) {
    throw new Response('Customer order not found', { status: 404 });
  }
  return {
    customerOrder,
    companyInfo: await fetchCompanyInfo(undefined, true),
  };
};

export interface CustomerOrderLoaderData {
  billingAccounts?: BillingAccountsResponse;
  customerOrder?: CustomerOrder;
  changeRequestDetails?: ChangeRequestDetails;
  additionalInfo?: CustomerOrderAdditionalInfo;
  companyInfo?: CompanyInfoResponse;
  contacts?: ContactsResponse;
  customerOrderDescription?: string;
}

export interface EmployeeApprovalCustomerOrderLoaderData {
  customerOrder: CustomerOrder;
  additionalInfo?: CustomerOrderAdditionalInfo;
  companyInfo?: CompanyInfoResponse;
}

export interface CatalogProducts {
  phones: OnlineModelHeader[];
  accessories: OnlineModelHeader[];
  tablets: OnlineModelHeader[];
  computers: OnlineModelHeader[];
  networkEquipment: OnlineModelHeader[];
}

export interface CatalogProductCodes {
  phones: string[];
  accessories: string[];
  tablets: string[];
  computers: string[];
  networkEquipment: string[];
}

export interface CatalogProductLoaderData {
  allOnlineModelCodes: string[];
  onlineModels: CatalogProducts;
  onlineModelCodes: CatalogProductCodes;
}

const loadChangeRequest = async (orderId: string, path: string, mdmId?: string): Promise<CustomerOrderLoaderData> => {
  const changeRequestDetails = await fetchChangeRequestDetails(orderId);
  if (!changeRequestDetails) {
    throw new Response('Change request details not found', { status: 404 });
  }

  const crdPending = changeRequestDetails.status === CustomerOrderStatus.PENDING_APPROVAL;
  const crdReqOpts = { mdmId, isEmployee: false };
  return {
    changeRequestDetails: changeRequestDetails,
    additionalInfo: crdPending ? await fetchCustomerOrderAdditionalInfo(orderId, false, mdmId) : undefined,
    companyInfo: await fetchCompanyInfo(mdmId, isEoe(path)),
    billingAccounts: await fetchBillingAccounts(
      { useSearchService: true, sourceSystem: SourceSystem.SFDC },
      crdReqOpts
    ),
    contacts: await fetchContacts({ useSearchService: true, offset: 0 }, crdReqOpts),
  };
};

const loadCustomerOrder = async (orderId: string, path: string, mdmId?: string): Promise<CustomerOrderLoaderData> => {
  if (!isSfdcOrder(orderId)) {
    const esResponse = await fetchCustomerOrders({ customerOrderDisplayId: orderId, useSearchService: true }, false);
    const esResult = esResponse.searchResults?.[0]?.result;
    if (!esResult) {
      throw new Response('Customer order not found', { status: 404 });
    }
    return {
      customerOrder: {
        customerOrderId: esResult.customerOrderDisplayId ?? '',
        customerOrderDisplayId: esResult.customerOrderDisplayId ?? '',
        billingAccountId: '',
        billingAccountDisplayId: '',
        deliveryOrders: [],
        status: esResult.status,
        customerOrderContact: esResult.customerOrderContactName,
        created: esResult.created ?? 0,
        lastModified: esResult.lastModified ?? 0,
        orderTopic: esResult.orderTopic,
        customerOrderType: esResult.customerOrderType,
        customerOrderDescription: esResult.customerOrderDescription,
      },
      customerOrderDescription: esResult.customerOrderDescription,
      companyInfo: await fetchCompanyInfo(mdmId, isEoe(path)),
    };
  }

  const sfdcResponse = await fetchCustomerOrders({ customerOrderDisplayId: orderId }, false, mdmId);
  const customerOrder = sfdcResponse.customerOrders?.[0] as CustomerOrder;
  if (!customerOrder) {
    throw new Response('Customer order not found', { status: 404 });
  }

  const pending = customerOrder.status === CustomerOrderStatus.PENDING_APPROVAL;
  const reqOpts = { mdmId, isEmployee: false };
  return {
    customerOrder,
    additionalInfo: pending ? await fetchCustomerOrderAdditionalInfo(orderId, false, mdmId) : undefined,
    companyInfo: await fetchCompanyInfo(mdmId, isEoe(path)),
    billingAccounts: await fetchBillingAccounts({ useSearchService: true, sourceSystem: SourceSystem.SFDC }, reqOpts),
    contacts: await fetchContacts({ useSearchService: true, offset: 0 }, reqOpts),
  };
};

export const customerOrderLoader = async ({
  params,
  request,
}: LoaderFunctionArgs): Promise<CustomerOrderLoaderData> => {
  const orderId = params.orderId;
  if (orderId === undefined) {
    throw new Error('missing customerOrderId');
  }
  const mdmId = getMdmIdFromRequest(request);
  const path = new URL(request.url).pathname;

  if (isChangeRequestId(orderId)) {
    return loadChangeRequest(orderId, path, mdmId);
  }
  return loadCustomerOrder(orderId, path, mdmId);
};

export const orderDetailCoCoLoader = async ({
  params,
  request,
}: LoaderFunctionArgs): Promise<CocoProductOrderDetail> => {
  const orderId = params.orderId;
  if (orderId === undefined) {
    throw new Error('missing customerOrderId');
  }
  const mdmId = getMdmIdFromRequest(request);
  return fetchCocoOrder(orderId, mdmId);
};

export const orderHistoryDetailCoCoLoader = (args: LoaderFunctionArgs) =>
  isLegacyOrder() ? customerOrderLoader(args) : orderDetailCoCoLoader(args);

export const employeeApprovalCustomerOrderLoader = async ({
  params,
  request,
}: LoaderFunctionArgs): Promise<EmployeeApprovalCustomerOrderLoaderData> => {
  if (params.orderId === undefined) {
    throw new Error('missing customerOrderId');
  }
  const mdmId = getMdmIdFromRequest(request);
  const path = new URL(request.url).pathname;

  const resp = await fetchCustomerOrders({ customerOrderDisplayId: params.orderId }, true, mdmId);
  const customerOrder = resp.customerOrders?.[0] as CustomerOrder;
  if (!customerOrder) {
    throw new Response('Customer order not found', { status: 404 });
  }
  const pending = customerOrder.status === CustomerOrderStatus.PENDING_APPROVAL || isEoePendingReviewPath(path);
  return {
    customerOrder,
    additionalInfo: pending ? await fetchCustomerOrderAdditionalInfo(params.orderId, true, mdmId) : undefined,
    companyInfo: await fetchCompanyInfo(mdmId, true),
  };
};

export interface DeliveryOrderLoaderData {
  customerOrder: CustomerOrder;
}

export const deliveryOrderLoader = async ({
  params,
  request,
}: LoaderFunctionArgs): Promise<DeliveryOrderLoaderData> => {
  if (params.orderId === undefined) {
    throw new Error('missing orderId');
  }
  if (params.deliveryId === undefined) {
    throw new Error('missing deliveryId');
  }
  const mdmId = getMdmIdFromRequest(request);
  const path = new URL(request.url).pathname;

  const resp = await fetchCustomerOrders({ customerOrderDisplayId: params.orderId }, isEoe(path), mdmId);
  const customerOrder = resp.customerOrders?.[0] as CustomerOrder;
  if (!customerOrder || !customerOrder.deliveryOrders.some(e => e.deliveryOrderDisplayId === params.deliveryId)) {
    throw new Response('Customer order not found', { status: 404 });
  }
  return { customerOrder };
};

export const dnsRecordHistoryLoader = ({ params }: LoaderFunctionArgs) =>
  fetchDnsRecordHistory(params.subscriptionId!, Number(params.historyId!));

export const dnsRecordsHistoryLoader = createDnsRecordsLoader(fetchDnsRecordsHistory, {
  [TableUrlParams.SORT]: 'id',
  [TableUrlParams.ORDER]: TableSortOrder.DESC,
  [TableUrlParams.LIMIT]: '30',
});

export const getDnsRecords = createDnsRecordsLoader(fetchDnsRecords, {
  [TableUrlParams.SORT]: 'type',
  [TableUrlParams.ORDER]: TableSortOrder.ASC,
  [TableUrlParams.LIMIT]: '30',
});

export interface SubscriptionsLoaderData {
  subscriptions: SubscriptionsResponse;
  aggregations: SubscriptionAggregationsResponse;
}

export const broadbandSubscriptionsLoader = async ({ request }: LoaderFunctionArgs) => {
  const query = buildSubscriptionsQuery(request, SubscriptionCategory.BROADBAND);
  const mdmId = getMdmIdFromRequest(request);
  return {
    subscriptions: await fetchSubscriptions(query, mdmId),
    aggregations: await fetchSubscriptionAggregates(SubscriptionCategory.BROADBAND, request, mdmId),
  };
};

export const serviceSubscriptionsLoader = ({ request }: LoaderFunctionArgs) => {
  const query = buildSubscriptionsQuery(request, SubscriptionCategory.SERVICE);
  const mdmId = getMdmIdFromRequest(request);
  return fetchSubscriptions(query, mdmId);
};

export interface ContactLoaderData {
  contact: Contact;
  deviceSubscriptions: SubscriptionSearchResponse[];
  voiceSubscriptions: SubscriptionSearchResponse[];
  broadbandSubscriptions: SubscriptionSearchResponse[];
  serviceSubscriptions: SubscriptionSearchResponse[];
  billingAccounts?: BillingAccountSearchResponse[];
  pendingActionsResponse: MoveContactPendingActionsResponse;
  invitation?: ContactInvitation;
  serviceAgreementsResponse?: GetServiceAgreementsResponse;
}

export const contactLoader = async ({ params, request }: LoaderFunctionArgs): Promise<ContactLoaderData> => {
  const contactMasterId = params.contactMasterId;
  if (!contactMasterId) {
    throw new Error('missing contactMasterId');
  }
  const mdmId = requireMdmIdFromRequest(request);
  const contact = await fetchContact({
    contactMasterId: contactMasterId!,
    mdmId,
  });
  const contactId = contact.contactId;
  if (!contactId) {
    throw new Error('missing contactId');
  }
  const [
    deviceResponse,
    voiceResponse,
    broadbandResponse,
    serviceResponse,
    billingAccountResponse,
    pendingActionsResponse,
    invitation,
    serviceAgreementsResponse,
  ] = await Promise.all([
    fetchSubscriptions(buildSubscriptionsQuery(request, SubscriptionCategory.DEVICE, undefined, contactId), mdmId),
    fetchSubscriptions(buildSubscriptionsQuery(request, SubscriptionCategory.VOICE, undefined, contactId), mdmId),
    fetchSubscriptions(buildSubscriptionsQuery(request, SubscriptionCategory.BROADBAND, undefined, contactId), mdmId),
    fetchSubscriptions(buildSubscriptionsQuery(request, SubscriptionCategory.SERVICE, undefined, contactId), mdmId),
    fetchContactsBillingAccounts(contactId),
    hasPendingActionsForContact(contactMasterId),
    isElisaIdV3Enabled() ? fetchContactInvitation(contactMasterId) : Promise.resolve(undefined),
    isElisaIdV3Enabled() && contact.person?.userName
      ? fetchServiceAgreements(contact.person?.userName)
      : Promise.resolve(undefined),
  ]);
  return {
    contact,
    deviceSubscriptions: deviceResponse.searchResults || [],
    voiceSubscriptions: voiceResponse.searchResults || [],
    broadbandSubscriptions: broadbandResponse.searchResults || [],
    serviceSubscriptions: serviceResponse.searchResults || [],
    billingAccounts: billingAccountResponse?.searchResults,
    pendingActionsResponse,
    invitation,
    serviceAgreementsResponse,
  };
};

export type MoveContactLoaderData = Omit<
  ContactLoaderData,
  'billingAccounts' | 'serviceSubscriptions' | 'invitation' | 'serviceAgreementsResponse'
>;

export const moveContactLoader = async ({ params, request }: LoaderFunctionArgs): Promise<MoveContactLoaderData> => {
  const contactMasterId = params.contactMasterId;
  if (!contactMasterId) {
    throw new Error('missing contactMasterId');
  }
  const mdmId = requireMdmIdFromRequest(request);
  const contact = await fetchContact({
    contactMasterId: contactMasterId!,
    mdmId,
  });
  const contactId = contact.contactId;
  if (!contactId) {
    throw new Error('missing contactId');
  }
  const [eppDeviceResponse, monthlyFeeDeviceResponse, voiceResponse, broadbandResponse, pendingActionsResponse] =
    await Promise.all([
      fetchSubscriptions(
        buildSubscriptionsQuery(request, undefined, [SubscriptionType.DEVICE], contactId, { eppFilter: 'All' }),
        mdmId
      ),
      fetchSubscriptions(
        buildSubscriptionsQuery(request, undefined, [SubscriptionType.DEVICE], contactId, { paymentType: 'Monthly' }),
        mdmId
      ),
      fetchSubscriptions(buildSubscriptionsQuery(request, undefined, [SubscriptionType.MOBILE], contactId), mdmId),
      fetchSubscriptions(
        buildSubscriptionsQuery(
          request,
          undefined,
          [SubscriptionType.BROADBAND, SubscriptionType.MOBILE_BROADBAND, SubscriptionType.MOBILE_M2M],
          contactId
        ),
        mdmId
      ),
      hasPendingActionsForContact(contactMasterId),
    ]);
  return {
    contact,
    deviceSubscriptions: [
      ...(eppDeviceResponse.searchResults || []),
      ...(monthlyFeeDeviceResponse.searchResults || []),
    ],
    voiceSubscriptions: voiceResponse.searchResults || [],
    broadbandSubscriptions: broadbandResponse.searchResults || [],
    pendingActionsResponse,
  };
};

export const deviceSubscriptionsLoader = async ({ request }: LoaderFunctionArgs) => {
  const query = buildSubscriptionsQuery(request, SubscriptionCategory.DEVICE);
  const mdmId = getMdmIdFromRequest(request);
  return {
    subscriptions: await fetchSubscriptions(query, mdmId),
    aggregations: await fetchSubscriptionAggregates(
      SubscriptionCategory.DEVICE,
      request,
      mdmId,
      undefined,
      undefined,
      true
    ),
  };
};

export const warrantyReceiptsLoader = async ({ request }: LoaderFunctionArgs) => {
  const mdmId = getMdmIdFromRequest(request);
  const [subscriptions, aggregations] = await Promise.all([
    fetchSubscriptions(
      { ...buildSubscriptionsQuery(request, SubscriptionCategory.DEVICE), billingAccountRecordType: 'CardPayment' },
      mdmId
    ),
    fetchSubscriptionAggregates(SubscriptionCategory.DEVICE, request, mdmId, undefined, 'CardPayment', true),
  ]);
  return { subscriptions, aggregations };
};

export const deviceSubscriptionLoader = async ({
  params,
  request,
}: SubscriptionLoaderArgs): Promise<DeviceSubscriptionLoaderResponse> => {
  const mdmId = requireMdmIdFromRequest(request);
  // getSubscriptionUrlId falls back to the internal subscriptionId when no displayId exists,
  // so the ES search-by-display-id lookup below is only valid for the 'SUB'-prefixed case.
  const isSubDisplayId = params.subscriptionId.startsWith('SUB');
  const [
    billingAccountsResponse,
    companyInfo,
    contactsResponse,
    hasPendingActions,
    subscription,
    subscriptionActionsResponse,
    billChannels,
    billingAccountRecordTypeResponse,
  ] = await Promise.all([
    fetchBillingAccounts({ useSearchService: true, sourceSystem: SourceSystem.SFDC }, { mdmId }),
    fetchCompanyInfo(mdmId),
    fetchContacts({ offset: 0 }, { mdmId }),
    hasPendingSubscriptionActions(params.subscriptionId, mdmId),
    fetchSubscription(SubscriptionCategory.DEVICE, params.subscriptionId, mdmId),
    fetchSubscriptionActions(
      mergeObjects(defaultRequest, {
        useSearchService: true,
        subscriptionDisplayId: params.subscriptionId,
        status: SubscriptionAction.StatusEnum.PENDING_APPROVAL,
      }),
      false,
      mdmId
    ),
    fetchBillChannels(mdmId),
    // Fetched in parallel with the SFDC-sourced subscription above (which lacks billingAccountRecordType)
    // so the warranty receipt link visibility is correct on first render, with no extra round trip.
    isSubDisplayId
      ? fetchSubscriptions(
          {
            subscriptionType: getSubscriptionTypes(SubscriptionCategory.DEVICE),
            subscriptionDisplayId: params.subscriptionId,
            useSearchService: true,
          },
          mdmId
        )
      : undefined,
  ]);
  const subscriptionScheduledChanges = await fetchSubscriptionScheduledChanges(subscription.subscriptionId);

  return {
    billingAccounts: billingAccountsResponse,
    companyInfo,
    contacts: contactsResponse.contacts || [],
    hasPendingActions,
    hasPendingAdminReview: subscriptionActionsResponse.searchResults?.length === 1,
    subscription,
    billChannels,
    subscriptionScheduledChanges,
    billingAccountRecordType: billingAccountRecordTypeResponse?.searchResults?.[0]?.result.billingAccountRecordType,
  };
};

export const deviceSubPostChangeRequestLoader = async ({
  params,
  request,
}: SubscriptionLoaderArgs): Promise<DeviceSubPostChangeRequestLoaderResponse> => {
  const mdmId = requireMdmIdFromRequest(request);
  const [companyInfo, hasPendingActions, subscription] = await Promise.all([
    fetchCompanyInfo(mdmId),
    hasPendingSubscriptionActions(params.subscriptionId, mdmId),
    fetchSubscription(SubscriptionCategory.DEVICE, params.subscriptionId, mdmId),
  ]);
  return { companyInfo, hasPendingActions, subscription };
};

export interface EoeDeviceSubscriptionLoaderData {
  contacts: ContactsResponse;
}

// EOE loaders must pass isEmployee: true. Without it, a KEY_USER (e.g. a multi-business admin) gets the
// unfiltered full contact list instead of the role-filtered approver list, see OFI-66246. Non-EOE flows
// must pass false, as the EMPLOYEE role would restrict admins to approver contacts only.
const fetchSortedContacts = async (
  mdmId: string | undefined,
  { isEmployee }: { isEmployee: boolean }
): Promise<ContactsResponse> => {
  const contacts = await fetchContacts({ offset: 0 }, { mdmId, isEmployee });
  return { ...contacts, contacts: sortContactsByFirstName(contacts.contacts || []) };
};

export const eoeDeviceSubscriptionLoader = async ({
  request,
}: LoaderFunctionArgs): Promise<EoeDeviceSubscriptionLoaderData> => {
  const mdmId = getMdmIdFromRequest(request) || getActiveAccountMasterId();
  const contacts = await fetchSortedContacts(mdmId, { isEmployee: true });
  return { contacts };
};

export type EoeDevicePathLoaderData = PersonBillingAccountLoaderData & EoeDeviceSubscriptionLoaderData;

export const eoeDevicePathLoader = async ({ request }: LoaderFunctionArgs): Promise<EoeDevicePathLoaderData> => {
  const mdmId = getMdmIdFromRequest(request) || getActiveAccountMasterId();
  const [billingAccounts, contacts] = await Promise.all([
    fetchBillingAccounts({ useSearchService: true }),
    fetchSortedContacts(mdmId, { isEmployee: true }),
  ]);
  return { billingAccounts, contacts };
};

export const voiceSubscriptionsLoader = async ({ request }: LoaderFunctionArgs) => {
  const query = buildSubscriptionsQuery(request, SubscriptionCategory.VOICE);
  const mdmId = getMdmIdFromRequest(request);
  return {
    subscriptions: await fetchSubscriptions(query, mdmId),
    aggregations: await fetchSubscriptionAggregates(SubscriptionCategory.VOICE, request, mdmId),
  };
};

export const deviceAddOnLoader = async ({
  params,
  request,
}: SubscriptionLoaderArgs): Promise<SubscriptionAddOnLoaderResponse> => {
  const mdmId = requireMdmIdFromRequest(request);
  const [hasPendingActions, subscription] = await Promise.all([
    hasPendingSubscriptionActions(params.subscriptionId, mdmId),
    fetchSubscription(SubscriptionCategory.DEVICE, params.subscriptionId, mdmId),
  ]);
  const addOn = (subscription?.details?.selectedAddOns || []).find(it => it.addOnCode === params.addonId);
  if (!addOn) {
    throw new Response('Subscription addon not found', { status: 404 });
  }
  return { addOn, hasPendingActions, subscription };
};

export const mobileSubAddOnLoader = async ({
  params,
  request,
}: SubscriptionLoaderArgs): Promise<SubscriptionAddOnLoaderResponse> => {
  const mdmId = requireMdmIdFromRequest(request);
  const [hasPendingActions, subscription] = await Promise.all([
    hasPendingSubscriptionActions(params.subscriptionId, mdmId),
    fetchSubscription(SubscriptionCategory.VOICE, params.subscriptionId, mdmId),
  ]);
  const addOn = (subscription?.details?.selectedAddOns || []).find(it => it.addOnCode === params.addonId);
  if (!addOn) {
    throw new Response('Subscription addon not found', { status: 404 });
  }
  return { addOn, hasPendingActions, subscription };
};

export interface ServiceSubscriptionLoaderResponse {
  companyInfo: CompanyInfoResponse;
  hasPendingActions: boolean;
  subscription: Subscription;
}

export const serviceSubscriptionLoader = async ({
  params,
  request,
}: SubscriptionLoaderArgs): Promise<ServiceSubscriptionLoaderResponse> => {
  const mdmId = requireMdmIdFromRequest(request);
  const [companyInfo, hasPendingActions, subscription] = await Promise.all([
    fetchCompanyInfo(mdmId),
    hasPendingSubscriptionActions(params.subscriptionId, mdmId),
    fetchSubscription(SubscriptionCategory.SERVICE, params.subscriptionId, mdmId),
  ]);
  return {
    companyInfo,
    hasPendingActions,
    subscription,
  };
};

export const dnsSubscriptionsLoader = async ({ request }: LoaderFunctionArgs) => {
  const query = buildSubscriptionsQuery(request, SubscriptionCategory.DOMAIN);
  return fetchSubscriptions(query);
};

export const contactsLoader = ({ request }: LoaderFunctionArgs) => {
  const { sort, offset, itemsPerPage, ...rest } = getSearchParams(request);
  const mdmId = getMdmIdFromRequest(request);
  return fetchContacts(
    mergeObjects(defaultRequest, {
      useSearchService: true,
      offset: Number(offset),
      limit: itemsPerPage,
      sort: getContactSort(sort),
      ...rest,
    }),
    { mdmId }
  );
};

export const subscriptionActionLoader = async ({
  params,
  request,
}: LoaderFunctionArgs): Promise<SubscriptionActionsResponse> => {
  if (params.requestId === undefined) {
    throw new Error('missing requestId');
  }
  const path = new URL(request.url).pathname;

  return fetchSubscriptionActions(
    {
      subscriptionActionDisplayId: params.requestId,
      own: isEoeOwnRedeemRequestsPath(path) ? true : undefined,
    },
    isEoe(path),
    getMdmIdFromRequest(request)
  );
};

export const subscriptionActionsLoader = ({ request }: LoaderFunctionArgs) => {
  const { offset, itemsPerPage, ...rest } = getSearchParams(request);
  const path = new URL(request.url).pathname;
  const mdmId = getMdmIdFromRequest(request);
  return fetchSubscriptionActions(
    mergeObjects(defaultRequest, {
      useSearchService: true,
      offset: Number(offset),
      limit: itemsPerPage,
      own: isEoeOwnRedeemRequestsPath(path) ? true : undefined,
      ...rest,
    }),
    isEoe(path),
    mdmId
  );
};

export const onlineModelsForBasketItemsLoader = async ({ request }: { request: Request }): Promise<OnlineModel[]> => {
  // The employee/punchout device-checkout cart lives in its own localStorage entry, separate from the
  // public-shop/OmaElisa 'shopping-basket' (which employee/punchout users never write to)
  const cartItems: CartItemType[] = JSON.parse(getShoppingCart());

  if (cartItems.length > 0) {
    const onlineModelCodes = [...new Set(cartItems.map(cartItem => cartItem.bundleItems[0].product.categoryGuid))];
    // Use the private, user-type-aware endpoint (employee/punchout/other) so priced models resolve; the
    // public endpoint 404s for employee/punchout-only codes, silently dropping them from the cart
    return await fetchV3PrivateOnlineModels(
      onlineModelCodes,
      undefined,
      undefined,
      false,
      undefined,
      new URL(request.url).pathname
    );
  }
  // no need to load anything if cart is empty..
  return [];
};

export interface PublicM2MBroadbandLoaderData {
  m2mSubscriptions: OnlineModel[];
}

export const publicM2MBroadbandPageLoader = async (): Promise<PublicM2MBroadbandLoaderData> => {
  const m2mOnlineModelCodes = [...mobileM2MSubscriptionCards4G, ...mobileM2MSubscriptionCards5G]
    .filter(sc => sc.publicPage?.onlineModelCode)
    .map(sc => sc.publicPage?.onlineModelCode)
    .filter((code): code is string => code !== undefined);
  const m2mSubscriptions = await fetchOnlineModels(m2mOnlineModelCodes);
  return {
    m2mSubscriptions,
  };
};

// Catalog creation must list every product across all categories. This loader only resolves the model
// codes per category; account-specific priced headers (the full display data) are fetched and cached by
// the online-models provider in the component, so the expensive private-header batch isn't refetched on
// every navigation.
export const catalogProductsLoader = async ({ request }: LoaderFunctionArgs): Promise<CatalogProductLoaderData> => {
  requireMdmIdFromRequest(request);
  const [phones, accessories, tablets, computers, networkEquipment] = await Promise.all([
    fetchOnlineModelHeaders(OnlineModelCategory.PHONE),
    fetchOnlineModelHeaders(OnlineModelCategory.ACCESSORIES),
    fetchOnlineModelHeaders(OnlineModelCategory.TABLET),
    fetchOnlineModelHeaders(OnlineModelCategory.COMPUTERS),
    fetchOnlineModelHeaders(OnlineModelCategory.NETWORK_EQUIPMENT),
  ]);
  const toCodes = (headers: OnlineModelHeader[]) => headers.map(header => header.onlineModelCode);
  const onlineModelCodes = {
    phones: toCodes(phones),
    accessories: toCodes(accessories),
    tablets: toCodes(tablets),
    computers: toCodes(computers),
    networkEquipment: toCodes(networkEquipment),
  };
  return {
    allOnlineModelCodes: [...new Set(Object.values(onlineModelCodes).flat())],
    onlineModels: {
      phones,
      accessories,
      tablets,
      computers,
      networkEquipment,
    },
    onlineModelCodes,
  };
};

export const virtualCatalogsLoader = ({ request }: LoaderFunctionArgs) => {
  const { params, offset, sort, itemsPerPage, ...rest } = getSearchParams(request);
  const mdmId = getMdmIdFromRequest(request);
  return fetchVirtualCatalogs(
    mergeObjects(defaultRequest, {
      useSearchService: true,
      productType: params.get('productType'),
      contractPeriod: params.get('contractPeriod'),
      status: params.get('status'),
      limit: itemsPerPage,
      offset: Number(offset),
      sort: sort || 'publishedOrDraftLastModified',
      ...rest,
    }),
    mdmId
  );
};

export interface VirtualCatalogDetailsLoaderData {
  allOnlineModelCodes: string[];
  billingAccounts: BillingAccountsResponse;
  catalog: VirtualCatalog;
  catalogHeaders: CatalogSearchResponse[];
  onlineModelHeadersByCategory: Record<string, OnlineModelHeader[]>;
  onlineModelCodesByCategory: Record<string, string[]>;
  secondaryAccounts: SecondaryAccount[];
}

export interface DeviceListTabsLoaderDataCoCo {
  deviceSelections: EmployeeProductSelectionResponse[];
  employeeProductLists: EmployeeProductListResponse[];
}

const getAllUserMdmIds = async (): Promise<string[]> => {
  const primaryMdmId = getPrimaryMdmId();
  if (!primaryMdmId) {
    return getRelatedCompanyHierarchyDetails();
  }
  const secondaryAccounts = await fetchSecondaryAccounts(primaryMdmId);
  return [...new Set([primaryMdmId, ...secondaryAccounts.map(account => account.accountMasterId)])];
};

export const deviceListTabsLoaderCoCo = async ({
  request,
}: LoaderFunctionArgs): Promise<DeviceListTabsLoaderDataCoCo> => {
  const tab = getSelectedDeviceListTab(new URL(request.url).searchParams.get(DEVICE_LIST_TAB_PARAM));
  const urlMdmId = getMdmIdFromRequest(request);
  if (tab === DeviceListTabCoCo.DEVICE_LISTS) {
    // Without mdmId in the URL, the lists of all the user's companies are shown
    const mdmIds = urlMdmId ? [urlMdmId] : await getAllUserMdmIds();
    if (mdmIds.length === 0) {
      return { deviceSelections: [], employeeProductLists: [] };
    }
    const [employeeProductLists, deviceSelections] = await Promise.all([
      fetchEmployeeProductLists(mdmIds),
      Promise.all(mdmIds.map(id => fetchEmployeeProductSelections(id))).then(selections => selections.flat()),
    ]);
    return { deviceSelections, employeeProductLists };
  }
  const mdmId = urlMdmId || getActiveAccountMasterId();
  return {
    deviceSelections: mdmId ? await fetchEmployeeProductSelections(mdmId) : [],
    employeeProductLists: [],
  };
};

const fetchDeviceVariantGroupsOfTypeCoCo = async (deviceType: DeviceVariantProductTypeCoCo) =>
  (
    await Promise.all(
      DEVICE_VARIANT_CATEGORIES.map(category =>
        fetchDeviceVariants(deviceType, { category: toDeviceVariantCategoryQuery(category) })
      )
    )
  ).flat();

export const loadDeviceVariantGroupsCoCo = async (): Promise<DeviceVariantGroupsByTypeCoCo> => {
  const [devices, eppDevices] = await Promise.all([
    fetchDeviceVariantGroupsOfTypeCoCo(DeviceVariantProductTypeCoCo.Device),
    fetchDeviceVariantGroupsOfTypeCoCo(DeviceVariantProductTypeCoCo.EPP),
  ]);
  return { [DeviceVariantProductTypeCoCo.Device]: devices, [DeviceVariantProductTypeCoCo.EPP]: eppDevices };
};

export interface DeviceSelectionCatalogLoaderDataCoCo {
  deviceVariantGroups: DeviceVariantGroupsByTypeCoCo;
}

export const deviceSelectionCatalogLoaderCoCo = async (): Promise<DeviceSelectionCatalogLoaderDataCoCo> => ({
  deviceVariantGroups: await loadDeviceVariantGroupsCoCo(),
});

export interface DeviceListNewLoaderDataCoCo {
  mdmId: string;
  // Used for the restricted billing account list, the default billing account is selected with SelectBillingAccountCoCo
  billingAccounts: BillingAccountCoco[];
  billChannels: BillChannel[];
  companyInfo: CompanyInfoResponse;
  customer: CustomerResponse;
  deviceSelections: EmployeeProductSelectionResponse[];
}

const loadDeviceListFormDataCoCo = async (mdmId: string): Promise<DeviceListNewLoaderDataCoCo> => {
  const [billingAccounts, billChannels, companyInfo, customer, deviceSelections] = await Promise.all([
    fetchBillingAccountsCoco(undefined, { mdmId }),
    fetchBillChannels(mdmId),
    fetchCompanyInfo(mdmId),
    fetchCustomer(mdmId),
    fetchEmployeeProductSelections(mdmId),
  ]);
  return { mdmId, billingAccounts, billChannels, companyInfo, customer, deviceSelections };
};

// Without a company in the URL ("All companies" on the device lists tab), the list is created for the active company
export const deviceListNewLoaderCoCo = async ({
  request,
}: LoaderFunctionArgs): Promise<DeviceListNewLoaderDataCoCo> => {
  const mdmId = getMdmIdFromRequest(request) || getActiveAccountMasterId();
  if (!mdmId) {
    throw new Error('missing mdmId');
  }
  return loadDeviceListFormDataCoCo(mdmId);
};

export interface DeviceListEditLoaderDataCoCo extends DeviceListNewLoaderDataCoCo {
  deviceList: EmployeeProductListResponse;
}

// The list is edited in the company it belongs to
export const deviceListEditLoaderCoCo = async ({
  request,
  params,
}: LoaderFunctionArgs): Promise<DeviceListEditLoaderDataCoCo> => {
  if (!params.deviceListUuid) {
    throw new Error('missing params.deviceListUuid');
  }
  const urlMdmId = getMdmIdFromRequest(request);
  const deviceList = await fetchEmployeeProductList(params.deviceListUuid, urlMdmId);
  const mdmId = deviceList.accountMdmId || urlMdmId || getActiveAccountMasterId();
  if (!mdmId) {
    throw new Error('missing mdmId');
  }
  return { ...(await loadDeviceListFormDataCoCo(mdmId)), deviceList };
};

export interface DeviceSelectionDetailsLoaderDataCoCo {
  deviceSelection: EmployeeProductSelectionResponse;
  deviceVariantGroups: DeviceVariantGroupsByTypeCoCo;
}

export const deviceSelectionDetailsLoaderCoCo = async ({
  request,
  params,
}: LoaderFunctionArgs): Promise<DeviceSelectionDetailsLoaderDataCoCo> => {
  if (!params.deviceSelectionUuid) {
    throw new Error('missing params.deviceSelectionUuid');
  }
  const [deviceSelection, deviceVariantGroups] = await Promise.all([
    fetchEmployeeProductSelection(params.deviceSelectionUuid, getMdmIdFromRequest(request)),
    loadDeviceVariantGroupsCoCo(),
  ]);
  return { deviceSelection, deviceVariantGroups };
};

export interface DeviceListDetailsLoaderDataCoCo {
  deviceList: EmployeeProductListResponse;
  billingAccounts: BillingAccountCoco[];
  deviceSelection?: EmployeeProductSelectionResponse;
  deviceVariantGroups?: DeviceVariantGroupsByTypeCoCo;
}

export const deviceListDetailsLoaderCoCo = async ({
  request,
  params,
}: LoaderFunctionArgs): Promise<DeviceListDetailsLoaderDataCoCo> => {
  if (!params.deviceListUuid) {
    throw new Error('missing params.deviceListUuid');
  }
  const urlMdmId = getMdmIdFromRequest(request);
  const deviceList = await fetchEmployeeProductList(params.deviceListUuid, urlMdmId);
  const mdmId = deviceList.accountMdmId || urlMdmId;
  const selectionUuid = deviceList.employeeProductSelectionUuid;
  const [billingAccounts, selectionData] = await Promise.all([
    // Billing accounts are only needed to show the default billing account with its number instead of its id, so the
    // page is shown also when they can't be fetched
    mdmId ? fetchBillingAccountsCoco(undefined, { mdmId }).catch(() => []) : [],
    selectionUuid
      ? Promise.all([fetchEmployeeProductSelection(selectionUuid, mdmId), loadDeviceVariantGroupsCoCo()])
      : undefined,
  ]);
  const [deviceSelection, deviceVariantGroups] = selectionData ?? [];
  return { deviceList, billingAccounts, deviceSelection, deviceVariantGroups };
};

export const virtualCatalogDetailsLoader = async ({
  request,
  params,
}: LoaderFunctionArgs): Promise<VirtualCatalogDetailsLoaderData> => {
  const mdmId = requireMdmIdFromRequest(request);
  if (params.catalogCode === undefined) {
    throw new Error('missing params.catalogCode');
  }
  const [billingAccounts, catalogResponse, catalogHeadersResponse, secondaryAccounts] = await Promise.all([
    fetchBillingAccounts({ useSearchService: true, sourceSystem: SourceSystem.SFDC }, { mdmId }),
    fetchVirtualCatalog(params.catalogCode!, mdmId),
    fetchVirtualCatalogs(
      mergeObjects(defaultRequest, {
        useSearchService: true,
      })
    ),
    fetchSecondaryAccounts(mdmId),
  ]);
  const catalog = catalogResponse.items[0];
  const catalogHeaders = catalogHeadersResponse.searchResults || [];
  const allNeededProductCategories = collectProductCategoriesOfVirtualCatalog(catalog);
  // Resolve only the model codes per category. Priced headers (full display data) are fetched and cached
  // by the online-models provider in the component, so the expensive private-header batch isn't refetched
  // by this loader on every visit.
  const onlineModels = await Promise.all(allNeededProductCategories.map(category => fetchOnlineModelHeaders(category)));
  const onlineModelHeadersByCategory: Record<string, OnlineModelHeader[]> = {};
  const onlineModelCodesByCategory: Record<string, string[]> = {};
  allNeededProductCategories.forEach((category, i) => {
    const headers = onlineModels[i] || [];
    onlineModelHeadersByCategory[category] = headers;
    onlineModelCodesByCategory[category] = headers.map(header => header.onlineModelCode);
  });
  return {
    allOnlineModelCodes: [...new Set(Object.values(onlineModelCodesByCategory).flat())],
    billingAccounts,
    catalog,
    catalogHeaders,
    onlineModelHeadersByCategory,
    onlineModelCodesByCategory,
    secondaryAccounts,
  };
};
export const invoiceLoader = async ({ params, request }: LoaderFunctionArgs): Promise<InvoiceLoaderResponse> => {
  if (params.invoiceId === undefined) {
    throw new Error('missing invoiceId');
  }
  // Use getInvoicePaymentMdmId() as fallback because after returning from a payment the redirect URL temporarily
  // lacks mdmId. The mdmId is added in InvoicePath after handling the payment return flow.
  const mdmId = getMdmIdFromRequest(request) || getInvoicePaymentMdmId();
  if (!mdmId) {
    throw new Error('missing mdmId');
  }

  const [invoicesResponse, billChannels, billingAccountsResponse] = await Promise.all([
    fetchInvoice(params.invoiceId, mdmId),
    fetchBillChannels(mdmId),
    fetchBillingAccounts({ offset: 0, useSearchService: true }, { mdmId }),
  ]);
  const invoice = invoicesResponse?.invoices?.[0] as Invoice;
  if (!invoice) {
    throw new Response('Invoice not found', { status: 404 });
  }
  const openSupportCases = await fetchOpenSupportCases({ search: invoice.invoiceDisplayId }, mdmId);
  const mappedSupportCases =
    openSupportCases?.searchResults?.map((res: SupportCasesSearchResponse) => res.result) || [];
  const billingAccount =
    billingAccountsResponse?.billingAccounts?.find(ba => ba.billingAccountId === invoice.billingAccountId) ??
    invoicesResponse.billingAccounts?.find(ba => ba.billingAccountId === invoice.billingAccountId);
  return {
    invoice,
    billChannels: billChannels || [],
    openSupportCases: mappedSupportCases,
    billingAccount: billingAccount,
  };
};

export interface InvoicesLoaderData {
  invoices: InvoicesResponse;
  supportCases: SupportCasesResponse;
}

export const invoicesLoader = async ({ request }: LoaderFunctionArgs): Promise<InvoicesLoaderData> => {
  const { itemsPerPage, offset = 0, sort = 'due', order = 'desc', search } = getSearchParams(request);
  const mdmId = getMdmIdFromRequest(request);
  const { paidInvoicesOnly, openInvoicesOnly } = getFilterInvoicesSearchParams(request);

  return {
    invoices: await fetchInvoices(
      {
        useSearchService: true,
        search,
        order,
        limit: itemsPerPage,
        offset: Number(offset),
        sort,
        paidInvoicesOnly,
        openInvoicesOnly,
      },
      mdmId
    ),
    supportCases: await fetchOpenSupportCases({ search, order: 'desc', offset: 0 }, mdmId),
  };
};

export interface DocumentsLoaderData {
  documents: InvoiceDocumentsResponse;
}

export const documentsLoader = async ({ request }: LoaderFunctionArgs): Promise<DocumentsLoaderData> => {
  const { itemsPerPage, offset = 0, sort = 'created', order = 'desc', search } = getSearchParams(request);
  const mdmId = getMdmIdFromRequest(request);
  return {
    documents: await fetchInvoiceDocuments(
      {
        useSearchService: true,
        offset: Number(offset),
        sort,
        order,
        search,
        limit: itemsPerPage,
      },
      mdmId
    ),
  };
};

export interface FixedBBRootLoaderData {
  onlineModels?: OnlineModel[];
  errors?: CommonError[];
}

export const fixedBBRootLoader = async () => {
  try {
    const tuplaModel = await fetchOnlineModelForCode(ModelType.Yritystupla);
    const broadbandModel = await fetchOnlineModelForCode(ModelType.MobileBroadband);
    // This filtering matches getOnlineModelFilteringOutNonOfferAddOns's usage in reducerUtils.ts
    const filteredModel = getOnlineModelFilteringOutNonOfferAddOns(broadbandModel);
    const mergedModels = mergeArrays<OnlineModel>('onlineModelCode', 'lastModified', [tuplaModel], [filteredModel]);

    return {
      onlineModels: mergedModels,
    };
  } catch (error) {
    return {
      errors: convertToCommonErrors(error.message, error.status, error.json()),
    };
  }
};

export interface FixedBBCheckoutLoaderData {
  preLoadedAddOnRules: AddOnRule[];
  addOnRules: AddOnRulesResponse;
  mobilePbxAddOnRules: AddOnRulesResponse;
  nettiAddOnRules: AddOnRulesResponse;
  netti4GAddOnRules: AddOnRulesResponse;
  holidays: Date[];
  billChannels: BillChannel[];
  contacts: ContactsResponse;
  billingAccounts: BillingAccountsResponse;
}

const fetchFixedBBCheckoutPrivateData = async (
  mdmId?: string
): Promise<Pick<FixedBBCheckoutLoaderData, 'contacts' | 'billingAccounts'>> => {
  const emptyContacts: ContactsResponse = { total: 0, contacts: [] };
  const emptyBillingAccounts: BillingAccountsResponse = { total: 0 };
  if (!mdmId) {
    return { contacts: emptyContacts, billingAccounts: emptyBillingAccounts };
  }
  const [contacts, billingAccounts] = await Promise.all([
    fetchSortedContacts(mdmId, { isEmployee: false }),
    fetchBillingAccounts({ useSearchService: true, sourceSystem: SourceSystem.SFDC }, { mdmId }),
  ]);
  return { contacts, billingAccounts };
};

export const fixedBBCheckoutLoader = async ({ request }: LoaderFunctionArgs): Promise<FixedBBCheckoutLoaderData> => {
  const mdmId = getMdmIdFromRequest(request) || getActiveAccountMasterId();
  const [
    addOnRules,
    mobilePbxAddOnRules,
    nettiAddOnRules,
    netti4GAddOnRules,
    holidays,
    billChannels,
    { contacts, billingAccounts },
  ] = await Promise.all([
    fetchAddOnRules(),
    fetchAddOnRules(SubscriptionType.MOBILE_PBX),
    fetchAddOnRules(AddOnRulesSubscriptionType.ELISA_NETTI),
    fetchAddOnRules(AddOnRulesSubscriptionType.ELISA_NETTI_4G),
    fetchHolidays(),
    fetchBillChannels(mdmId),
    fetchFixedBBCheckoutPrivateData(mdmId),
  ]);

  return {
    preLoadedAddOnRules: createPreLoadedAddOnRules(nettiAddOnRules, netti4GAddOnRules),
    addOnRules,
    mobilePbxAddOnRules,
    nettiAddOnRules,
    netti4GAddOnRules,
    holidays: holidays.dates.map(date => new Date(date)),
    billChannels,
    contacts,
    billingAccounts,
  };
};

export interface CheckoutRootLoaderData {
  models?: OnlineModel[];
  billChannels: BillChannel[];
  contacts: ContactsResponse;
  billingAccounts: BillingAccountsResponse;
}

export const checkoutRootLoader = async ({ request }: LoaderFunctionArgs): Promise<CheckoutRootLoaderData> => {
  const mdmId = getMdmIdFromRequest(request) || getActiveAccountMasterId();
  const [models, billChannels, contacts, billingAccounts] = await Promise.all([
    onlineModelsForBasketItemsLoader({ request }),
    fetchBillChannels(mdmId),
    fetchSortedContacts(mdmId, { isEmployee: true }),
    fetchBillingAccounts({ useSearchService: true }),
  ]);
  return {
    models,
    billChannels,
    contacts,
    billingAccounts,
  };
};

export interface PunchoutCheckoutLoaderData {
  models: OnlineModel[];
}

export const punchoutCheckoutLoader = async ({ request }: LoaderFunctionArgs): Promise<PunchoutCheckoutLoaderData> => ({
  models: await onlineModelsForBasketItemsLoader({ request }),
});

// Route-scoped factory: category is known at route-definition time, so the loader for a given
// store-products route only fetches the headers for that one category.
export const categoryOnlineModelHeadersLoader = (category: OnlineModelCategory) => (): Promise<OnlineModelHeader[]> =>
  fetchOnlineModelHeaders(category);

export interface OrderVoiceSubLoaderData {
  voiceModel: OnlineModel;
  addOnRules: AddOnRulesResponse;
}

export interface OrderMobileBBSubLoaderData {
  addOnRules: AddOnRulesResponse;
  mobileBroadbandModel: OnlineModel;
  elisaNettiModel: OnlineModel;
}

export interface OrderM2MLoaderData {
  addOnRules: AddOnRulesResponse;
  m2mModel: OnlineModel;
}

export interface OrderSubscriptionCommonLoaderData {
  companyInfo: CompanyInfoResponse;
  onlineModels: OnlineModel[];
  contacts: ContactsResponse;
  addOnRules: AddOnRulesResponse;
}

export const orderSubscriptionCommonLoader = async ({ request }: LoaderFunctionArgs) => {
  const mdmId = getMdmIdFromRequest(request) || getActiveAccountMasterId() || getPrimaryMdmId() || undefined;

  const [companyData, contacts, addOnRules] = await Promise.all([
    fetchCompanyInfoAndEnabledOnlineModels(mdmId),
    fetchContacts({ useSearchService: true, offset: 0 }, { mdmId }),
    fetchAddOnRules(),
  ]);
  return {
    companyInfo: companyData.companyInfo,
    onlineModels: companyData.onlineModels.map(getOnlineModelFilteringOutNonOfferAddOns),
    contacts,
    addOnRules,
  };
};

export interface OrderMobileBBCommonLoaderData extends OrderSubscriptionCommonLoaderData, OrderMobileBBSubLoaderData {}

export const orderMobileBBCommonLoader = async ({
  request,
}: LoaderFunctionArgs): Promise<OrderMobileBBCommonLoaderData> => {
  const mdmId = getMdmIdFromRequest(request) || getActiveAccountMasterId() || getPrimaryMdmId() || undefined;

  const [companyData, contacts, addOnRulesBasic, addOnRulesNetti4G, mobileBroadbandModel, elisaNettiModel] =
    await Promise.all([
      fetchCompanyInfoAndEnabledOnlineModels(mdmId),
      fetchContacts({ useSearchService: true, offset: 0 }, { mdmId }),
      fetchAddOnRules(),
      fetchAddOnRules(AddOnRulesSubscriptionType.ELISA_NETTI_4G),
      fetchOnlineModelForCode(ModelType.MobileBroadband),
      fetchOnlineModelForCode(ModelType.ElisaNetti),
    ]);

  const combinedAddOnRules = combineAddOnRulesResponses(addOnRulesBasic, addOnRulesNetti4G);

  return {
    companyInfo: companyData.companyInfo,
    onlineModels: companyData.onlineModels.map(getOnlineModelFilteringOutNonOfferAddOns),
    contacts,
    addOnRules: combinedAddOnRules,
    mobileBroadbandModel: getOnlineModelFilteringOutNonOfferAddOns(mobileBroadbandModel),
    elisaNettiModel: getOnlineModelFilteringOutNonOfferAddOns(elisaNettiModel),
  };
};

export interface MobileSubscriptionLoaderResponse {
  addOnRules: AddOnRulesResponse;
  addOnRulesMobilePbx: AddOnRulesResponse;
  billingAccounts: BillingAccountsResponse;
  companyInfo: CompanyInfoResponse;
  contacts: Contact[];
  billChannels: BillChannel[];
  hasPendingActions: boolean;
  mobileIdContracts?: Contract[];
  subscription: Subscription;
  ringModel?: OnlineModel;
  subscriptionScheduledChanges?: GetSubscriptionScheduledChanges;
}

export const voiceSubscriptionLoader = async ({
  params,
  request,
}: SubscriptionLoaderArgs): Promise<MobileSubscriptionLoaderResponse> => {
  const mdmId = requireMdmIdFromRequest(request);
  const [
    subscription,
    hasPendingActions,
    companyInfo,
    billingAccounts,
    contactsResponse,
    mobileIdContractsResponse,
    ringModel,
    addOnRules,
    addOnRulesMobilePbx,
    billChannels,
  ] = await Promise.all([
    fetchSubscription(SubscriptionCategory.VOICE, params.subscriptionId, mdmId),
    hasPendingSubscriptionActions(params.subscriptionId, mdmId),
    fetchCompanyInfo(mdmId),
    fetchBillingAccounts({ useSearchService: true, sourceSystem: SourceSystem.SFDC }, { mdmId }),
    fetchContacts({ offset: 0 }, { mdmId }),
    fetchMobileIdContractsByMdmId(mdmId),
    fetchV3PrivateOnlineModels([ModelType.Ring], undefined, undefined, true, mdmId).then(([model]) => model),
    fetchAddOnRules(),
    fetchAddOnRules(SubscriptionType.MOBILE_PBX),
    fetchBillChannels(mdmId),
  ]);
  const subscriptionScheduledChanges = await fetchSubscriptionScheduledChanges(subscription.subscriptionId);

  return {
    addOnRules,
    addOnRulesMobilePbx,
    billingAccounts,
    companyInfo,
    contacts: contactsResponse.contacts || [],
    mobileIdContracts: mobileIdContractsResponse.contracts,
    hasPendingActions,
    subscription,
    ringModel,
    billChannels,
    subscriptionScheduledChanges,
  };
};

export const broadbandSubscriptionLoader = async ({
  params,
  request,
}: SubscriptionLoaderArgs): Promise<MobileSubscriptionLoaderResponse> => {
  const mdmId = requireMdmIdFromRequest(request);
  const [
    subscription,
    hasPendingActions,
    companyInfo,
    billingAccounts,
    contactsResponse,
    addOnRules,
    addOnRulesMobilePbx,
    billChannels,
  ] = await Promise.all([
    fetchSubscription(SubscriptionCategory.BROADBAND, params.subscriptionId, mdmId),
    hasPendingSubscriptionActions(params.subscriptionId, mdmId),
    fetchCompanyInfo(mdmId),
    fetchBillingAccounts({ useSearchService: true, sourceSystem: SourceSystem.SFDC }, { mdmId }),
    fetchContacts({ offset: 0 }, { mdmId }),
    fetchAddOnRules(),
    fetchAddOnRules(SubscriptionType.MOBILE_PBX),
    fetchBillChannels(mdmId),
  ]);
  const subscriptionScheduledChanges = await fetchSubscriptionScheduledChanges(subscription.subscriptionId);

  return {
    addOnRules,
    addOnRulesMobilePbx,
    billingAccounts,
    companyInfo,
    contacts: contactsResponse.contacts || [],
    hasPendingActions,
    subscription,
    billChannels,
    subscriptionScheduledChanges,
  };
};

export interface MobileSubAttachRingLoaderData {
  addOnRules: AddOnRulesResponse;
  companyInfo?: CompanyInfoResponse;
  onlineModel: OnlineModel;
  hasPendingActions: boolean;
  subscription: Subscription;
  numberRanges: NumberRangeData[];
}

export interface NumberRangeData {
  rangeId: string;
  allNumbers?: string[];
  displayedNumbers?: string[];
  numberCategory?: NumberRangeResponse.NumberCategoryEnum;
  errors?: CommonError[];
}

export interface MobileSubAttachVakioLoaderData {
  companyInfo?: CompanyInfoResponse;
  hasPendingActions: boolean;
  subscription: Subscription;
  numberRanges: NumberRangeData[];
}

export interface MobileSubPostChangeRequestLoaderResponse {
  subscription: Subscription;
  hasPendingActions: boolean;
  companyInfo: CompanyInfoResponse;
}

export const mobileSubPostChangeRequestLoader = async ({
  params,
  request,
}: SubscriptionLoaderArgs): Promise<MobileSubPostChangeRequestLoaderResponse> => {
  const mdmId = requireMdmIdFromRequest(request);
  const [companyInfo, hasPendingActions, subscription] = await Promise.all([
    fetchCompanyInfo(mdmId),
    hasPendingSubscriptionActions(params.subscriptionId, mdmId),
    fetchSubscription(SubscriptionCategory.VOICE, params.subscriptionId, mdmId),
  ]);
  return {
    companyInfo,
    hasPendingActions,
    subscription,
  };
};

export const mobileSubAttachVakioLoader = async ({
  params,
  request,
}: SubscriptionLoaderArgs): Promise<MobileSubAttachVakioLoaderData> => {
  const mdmId = requireMdmIdFromRequest(request);
  const [companyInfo, hasPendingActions, subscription] = await Promise.all([
    fetchCompanyInfo(mdmId),
    hasPendingSubscriptionActions(params.subscriptionId, mdmId),
    fetchSubscription(SubscriptionCategory.VOICE, params.subscriptionId, mdmId),
  ]);
  const numberRangeIds = (companyInfo.pbxSolutions ?? [])
    .filter(solution => solution.subscriptionType === SubscriptionType.MOBILE_PBX_LITE)
    .flatMap(solution => solution.details?.mobilePbx?.numberRanges)
    .filter(numberRange => numberRange !== undefined)
    .map(numberRange => numberRange.rangeId);

  const numberRanges = await Promise.all(numberRangeIds.map(async range => fetchNumberRange(range, mdmId)));
  return {
    companyInfo,
    hasPendingActions,
    subscription,
    numberRanges: numberRanges,
  };
};

export interface SubscriptionUpdateLoaderData {
  addOnRules: AddOnRulesResponse;
  companyInfo: CompanyInfoResponse;
  onlineModels: OnlineModel[];
  subscription: Subscription;
}

export const mobileSubUpdateLoader = async ({
  params,
  request,
}: SubscriptionLoaderArgs): Promise<SubscriptionUpdateLoaderData> => {
  const mdmId = requireMdmIdFromRequest(request);
  const [addOnRules, companyInfo, subscription, yrityspuheNewModel, voiceSMEModel, voiceModel, pakettiModel] =
    await Promise.all([
      fetchAddOnRules(),
      fetchCompanyInfo(mdmId),
      fetchSubscription(SubscriptionCategory.VOICE, params.subscriptionId, mdmId),
      fetchOnlineModelForCode(ModelType.YrityspuheUusi),
      fetchOnlineModelForCode(ModelType.VoiceSME),
      fetchOnlineModelForCode(ModelType.Voice),
      fetchOnlineModelForCode(ModelType.Yrityspaketti),
    ]);
  const onlineModels = [yrityspuheNewModel, voiceSMEModel, voiceModel, pakettiModel];
  return {
    addOnRules,
    companyInfo,
    onlineModels: onlineModels.map(getOnlineModelFilteringOutNonOfferAddOns),
    subscription,
  };
};

export const broadbandSubUpdateLoader = async ({
  params,
  request,
}: SubscriptionLoaderArgs): Promise<SubscriptionUpdateLoaderData> => {
  const mdmId = requireMdmIdFromRequest(request);
  const [addOnRules, companyInfo, subscription, mobileBroadbandModel] = await Promise.all([
    fetchAddOnRules(),
    fetchCompanyInfo(mdmId),
    fetchSubscription(SubscriptionCategory.BROADBAND, params.subscriptionId, mdmId),
    fetchOnlineModelForCode(ModelType.MobileBroadband),
  ]);
  return {
    addOnRules,
    companyInfo,
    onlineModels: [getOnlineModelFilteringOutNonOfferAddOns(mobileBroadbandModel)],
    subscription,
  };
};

export const broadbandSubUpdateM2MLoader = async ({
  params,
  request,
}: SubscriptionLoaderArgs): Promise<SubscriptionUpdateLoaderData> => {
  const mdmId = requireMdmIdFromRequest(request);
  const [addOnRules, companyInfo, subscription, mobileBroadbandModel] = await Promise.all([
    fetchAddOnRules(),
    fetchCompanyInfo(mdmId),
    fetchSubscription(SubscriptionCategory.BROADBAND, params.subscriptionId, mdmId),
    fetchOnlineModelForCode(ModelType.MobileM2M),
  ]);
  return {
    addOnRules,
    companyInfo,
    onlineModels: [getOnlineModelFilteringOutNonOfferAddOns(mobileBroadbandModel)],
    subscription,
  };
};

export interface MobileSubUpdateConfirmationLoaderData {
  addOnRules: AddOnRulesResponse;
  addOnRulesMobilePbx: AddOnRulesResponse;
  pbxSolutions?: Subscription[];
}

export const mobileSubUpdateConfirmationLoader = async ({
  request,
}: LoaderFunctionArgs): Promise<MobileSubUpdateConfirmationLoaderData> => {
  const mdmId = requireMdmIdFromRequest(request);
  const [addOnRules, addOnRulesMobilePbx, companyInfo] = await Promise.all([
    fetchAddOnRules(),
    fetchAddOnRules(SubscriptionType.MOBILE_PBX),
    fetchCompanyInfo(mdmId),
  ]);
  return {
    addOnRules,
    addOnRulesMobilePbx,
    pbxSolutions: companyInfo.pbxSolutions,
  };
};

export const mobileSubAttachRingLoader = async ({
  params,
  request,
}: SubscriptionLoaderArgs): Promise<MobileSubAttachRingLoaderData> => {
  const mdmId = requireMdmIdFromRequest(request);
  const [addOnRules, companyInfo, hasPendingActions, onlineModel, subscription] = await Promise.all([
    fetchAddOnRules(SubscriptionType.MOBILE_PBX),
    fetchCompanyInfo(mdmId),
    hasPendingSubscriptionActions(params.subscriptionId, mdmId),
    fetchV3PrivateOnlineModels([ModelType.Ring], undefined, undefined, true, mdmId).then(([model]) => model),
    fetchSubscription(SubscriptionCategory.VOICE, params.subscriptionId, mdmId),
  ]);
  const numberRangeIds = (companyInfo?.pbxSolutions ?? [])
    .filter(solution => solution.subscriptionType === SubscriptionType.MOBILE_PBX)
    .flatMap(solution => solution.details?.mobilePbx?.numberRanges)
    .filter(numberRange => numberRange !== undefined)
    .map(numberRange => numberRange.rangeId);

  const numberRanges = await Promise.all(numberRangeIds.map(async range => fetchNumberRange(range, mdmId)));
  return {
    addOnRules,
    companyInfo,
    onlineModel,
    hasPendingActions,
    subscription,
    numberRanges,
  };
};

export const mfaDetailsLoader = async (): Promise<MfaDetailsResponse> => {
  return await fetchMfaDetails();
};

export interface CatalogSummaryLoaderData {
  billingAccounts: BillingAccountsResponse;
}

export const catalogSummaryLoader = async ({ request }: LoaderFunctionArgs): Promise<CatalogSummaryLoaderData> => {
  const mdmId = requireMdmIdFromRequest(request);
  return {
    billingAccounts: await fetchBillingAccounts({ sourceSystem: SourceSystem.SFDC, useSearchService: true }, { mdmId }),
  };
};

export type CompanyInfoLoaderData = CompanyInfoResponse & { mdmId: string };

export const companyInfoHomeLoader = async (): Promise<CompanyInfoLoaderData[]> => {
  const primaryMdmId = getPrimaryMdmId();
  if (!primaryMdmId) {
    throw new Response('Unauthorized', { status: 401 });
  }
  const [primaryAccount, secondaryCompanyInfos] = await Promise.all([
    fetchCompanyInfo(primaryMdmId),
    fetchAllSecondaryCompanyInfos(primaryMdmId),
  ]);
  const otherAccounts: CompanyInfoLoaderData[] = secondaryCompanyInfos.map(sa => ({
    ...sa.companyInfo,
    mdmId: sa.mdmId,
  }));
  return [...otherAccounts, { ...primaryAccount, mdmId: primaryMdmId }];
};

export type BroadbandSubPostChangeRequestLoaderResponse = MobileSubPostChangeRequestLoaderResponse;

export const broadbandSubPostChangeRequestLoader = async ({
  params,
  request,
}: SubscriptionLoaderArgs): Promise<BroadbandSubPostChangeRequestLoaderResponse> => {
  const mdmId = requireMdmIdFromRequest(request);
  const [companyInfo, hasPendingActions, subscription] = await Promise.all([
    fetchCompanyInfo(mdmId),
    hasPendingSubscriptionActions(params.subscriptionId, mdmId),
    fetchSubscription(SubscriptionCategory.BROADBAND, params.subscriptionId, mdmId),
  ]);
  return {
    companyInfo,
    hasPendingActions,
    subscription,
  };
};

export const broadbandSubAddOnLoader = async ({
  params,
  request,
}: SubscriptionLoaderArgs): Promise<SubscriptionAddOnLoaderResponse> => {
  const mdmId = requireMdmIdFromRequest(request);
  const [hasPendingActions, subscription] = await Promise.all([
    hasPendingSubscriptionActions(params.subscriptionId, mdmId),
    fetchSubscription(SubscriptionCategory.BROADBAND, params.subscriptionId, mdmId),
  ]);
  const addOn = (subscription?.details?.selectedAddOns || []).find(it => it.addOnCode === params.addonId);
  if (!addOn) {
    throw new Response('Subscription addon not found', { status: 404 });
  }
  return { addOn, hasPendingActions, subscription };
};

export const mobileIdContractsLoader = async (): Promise<Contract[]> => {
  const res = await fetchMobileIdContracts();
  return res.contracts || [];
};

export const newMobileIdContractLoader = async ({ request }: LoaderFunctionArgs): Promise<Contract[]> => {
  const mdmId = getMdmIdFromRequest(request) || getPrimaryMdmId();
  if (!mdmId) {
    throw new Error('missing mdmId');
  }
  const res = await fetchMobileIdContractsByMdmId(mdmId);
  return res.contracts || [];
};

export const mobileIdContractLoader = async ({ params }: LoaderFunctionArgs): Promise<Contract> => {
  if (params.contractDisplayId === undefined) {
    throw new Error('missing contractDisplayId');
  }
  return await fetchMobileIdContract(params.contractDisplayId);
};

export interface ReportsLoaderData {
  reports: ReportItemResponse[];
  companyInfo: CompanyInfoResponse;
  secondaryAccounts: SecondaryAccount[];
}

export const reportsLoader = async (): Promise<ReportsLoaderData> => {
  const primaryMdmId = getPrimaryMdmId();
  if (!primaryMdmId) {
    throw new Response('Unauthorized', { status: 401 });
  }
  const [reports, companyInfo, secondaryAccounts] = await Promise.all([
    fetchReports(),
    fetchCompanyInfo(primaryMdmId),
    fetchSecondaryAccounts(primaryMdmId),
  ]);
  return {
    reports,
    companyInfo,
    secondaryAccounts,
  };
};

export const companyInvitesLoader = async (): Promise<OnboardingRequestsResponse> => {
  return await fetchOnboardingRequests();
};

export const invitationsLoader = async (): Promise<Invitation[]> => {
  return await fetchInvitations();
};

export const notFoundLoader = () => {
  throw Response.json({ message: 'Not found' }, { status: 404 });
};

export interface SupportCaseFormLoaderData {
  billChannels: BillChannel[];
  companyInfo?: CompanyInfoResponse;
}

export const supportCaseFormLoader = async ({ request }: LoaderFunctionArgs): Promise<SupportCaseFormLoaderData> => {
  const primaryMdmId = getPrimaryMdmId();
  const mdmId = getMdmIdFromRequest(request) || primaryMdmId;

  const companyInfoMdmId = primaryMdmId ?? mdmId;
  const [companyInfo, billChannels] = await Promise.all([
    companyInfoMdmId ? fetchCompanyInfo(companyInfoMdmId) : undefined,
    fetchBillChannels(mdmId),
  ]);

  return { companyInfo, billChannels };
};

export const supportCaseFormPublicLoader = async ({
  request,
}: LoaderFunctionArgs): Promise<SupportCaseFormLoaderData> => {
  const billChannels = await fetchBillChannels(getMdmIdFromRequest(request));

  return { billChannels };
};

export const orderProductOfferingVoiceLoader = async (): Promise<ProductOfferingsResponse[]> => {
  return fetchProductOffersForOfferType(OfferType.VOICE);
};

export const vaihtoetuItemGroupsLoader = async (): Promise<VaihtoetuItemGroupList1[]> => {
  return fetchVaihtoetuItemGroups();
};

export const orderProductOfferingMobileBroadbandLoader = async (): Promise<ProductOfferingsResponse[]> => {
  return fetchProductOffersForOfferType(OfferType.MOBILE_BROADBAND);
};

export interface PublicDeviceOfferLoaderData {
  offer: ProductOfferingsResponse;
  allOffers: ProductOfferingsResponse[];
  slug: string;
}

export interface PublicPageOrDeviceLoaderData extends PageResponse {
  deviceOfferData?: PublicDeviceOfferLoaderData;
}

const getKauppaSlugsForOffer = (offer: ProductOfferingsResponse): string[] => {
  const rawNames = new Set<string>();
  const collectName = (value: unknown) => {
    if (typeof value === 'string' && value.trim()) {
      rawNames.add(value);
    }
  };

  if (typeof offer.name === 'string') {
    collectName(offer.name);
  } else {
    collectName((offer.name as { value?: unknown } | undefined)?.value);
    ((offer.name as { localizedValue?: Array<{ value?: unknown }> } | undefined)?.localizedValue ?? []).forEach(
      localized => collectName(localized?.value)
    );
  }

  const fullSlugs = Array.from(rawNames)
    .map(name => toKauppaSlug(name))
    .filter(Boolean);

  if (!fullSlugs.length) {
    return [];
  }

  const brand = offer.productSpecification?.find(
    (spec: { name: string }) => (typeof spec.name === 'string' ? spec.name.toLowerCase() : '') === 'brand'
  )?.value?.[0]?.value;
  const brandSlug = brand ? toKauppaSlug(brand) : '';

  const slugs = new Set<string>();
  fullSlugs.forEach(fullSlug => {
    slugs.add(fullSlug);
    if (brandSlug && fullSlug.startsWith(`${brandSlug}-`)) {
      slugs.add(fullSlug.slice(brandSlug.length + 1));
    }
  });

  return Array.from(slugs);
};

const isNotFoundResponse = (error: unknown): error is Response => error instanceof Response && error.status === 404;

const getDeviceSlugFromPathname = (pathname: string): string | undefined => {
  const pathSegments = pathname.split('/').filter(Boolean);
  return pathSegments.length === 1 ? pathSegments[0] : undefined;
};

// TODO: remove this once CMS page is implemented for Kauppa page. This is a temporary stub to allow Kauppa page to be rendered.
const createCoCoShopPageResponse = (): PageResponse => ({
  pageId: 'kauppa',
  meta: {
    title: 'Laitteet yrityksille edullisesti',
    description: '',
    ingress: '',
    language: 'fi',
    robots: 'index, follow',
    hideOnGoogle: 'false',
    hideLinksOnGoogle: 'false',
  },
  contentBlocks: [],
  siteStructureNodes: [
    { id: 1, name: 'Yrityksille', path: '/' },
    { id: 935, name: 'Verkkokauppa', path: paths.WEB_SHOP },
  ],
  onlineModels: [],
  onlineModelHeaders: [],
  breadcrumbs: [1, 935],
});

export const loadPublicDeviceOfferBySlug = async (
  slug: string,
  request: Request
): Promise<PublicDeviceOfferLoaderData> => {
  const normalizedSlug = slug.toLowerCase();
  if (!normalizedSlug) {
    throw new Response('Not Found', { status: 404 });
  }
  const origin = new URL(request.url).origin;
  const brandHint = normalizedSlug.split('-')[0];

  let allOffers =
    brandHint && brandHint.length > 1
      ? await fetchDeviceOffersForOfferType(OfferType.DEVICE, { brand: brandHint }, { origin }).catch(() => [])
      : [];

  let offer = allOffers.find(o => getKauppaSlugsForOffer(o).includes(normalizedSlug));

  if (!offer) {
    allOffers = await fetchDeviceOffersForOfferType(OfferType.DEVICE, undefined, { origin });
    offer = allOffers.find(o => getKauppaSlugsForOffer(o).includes(normalizedSlug));
  }

  if (!offer) {
    throw new Response('Device not found', { status: 404 });
  }
  return { offer, allOffers, slug: normalizedSlug };
};

export const publicDeviceOfferLoader = async ({
  params,
  request,
}: LoaderFunctionArgs): Promise<PublicDeviceOfferLoaderData> => {
  const slug = params.deviceSlug;
  if (!slug) {
    throw new Response('Not Found', { status: 404 });
  }
  return loadPublicDeviceOfferBySlug(slug, request);
};

export const employeeDeviceOfferLoaderCoCo = async ({
  params,
  request,
}: LoaderFunctionArgs): Promise<PublicDeviceOfferLoaderData> => {
  const slug = params.deviceName;
  if (!slug) {
    throw new Response('Device not found', { status: 404 });
  }
  const url = new URL(request.url);
  const offerId = url.searchParams.get('offerId');
  if (!offerId) {
    return loadPublicDeviceOfferBySlug(slug, request);
  }
  const [offer, allOffers] = await Promise.all([
    fetchProductOfferByOfferId(offerId, { origin: url.origin }),
    fetchDeviceOffersForOfferType(OfferType.DEVICE, undefined, { origin: url.origin }),
  ]);
  return { offer, allOffers, slug };
};

export const publicDeviceOffersLoader = async (
  { params, request }: LoaderFunctionArgs,
  deviceCategory?: string
): Promise<ProductOfferingsResponse[]> => {
  const origin = new URL(request.url).origin;
  const offerType = (deviceCategory ?? params.category ?? OfferType.DEVICE) as OfferType;
  return fetchDeviceOffersForOfferType(offerType, undefined, { origin });
};

export const publicPageOrDeviceLoader = async (
  args: LoaderFunctionArgs
): Promise<PageResponse | PublicPageOrDeviceLoaderData> => {
  const pathname = new URL(args.request.url).pathname;

  if (pathname === paths.WEB_SHOP) {
    return fetchPublicPageLoader(args);
  }

  const deviceSlug = getDeviceSlugFromPathname(pathname);
  if (deviceSlug) {
    try {
      const deviceOfferData = await loadPublicDeviceOfferBySlug(deviceSlug, args.request);
      return { ...createCoCoShopPageResponse(), deviceOfferData };
    } catch (error) {
      if (!isNotFoundResponse(error)) {
        throw error;
      }
    }
  }

  return fetchPublicPageLoader(args);
};

export const publicProductOfferByOfferIdLoader = async (offerId: string): Promise<ProductOfferingsResponse> => {
  return fetchProductOfferByOfferId(offerId);
};

export const orderProductOfferingSoftwareProductLoader = async (): Promise<ProductOfferingsResponse[]> => {
  return fetchProductOffersForOfferType(OfferType.SOFTWARE);
};
export interface PlaceProductOrderLoaderData {
  contacts: ContactsResponse;
  customerInfo: CustomerResponse;
  mdmId: string;
  productOffersInCart: ProductOfferingsResponse[];
  holidays: Date[];
  billChannels: BillChannel[];
}

const loadPlaceProductOrderData = async (scope: ShoppingBasketCoCoScope): Promise<PlaceProductOrderLoaderData> => {
  const mdmId = getActiveAccountMasterId();
  // Return stub data when mdmId is unavailable (e.g. server-side rendering or before auth completes).
  // PlaceOrderPath will redirect based on auth/basket state before this data is used.
  if (!mdmId) {
    return {
      contacts: {} as ContactsResponse,
      customerInfo: {} as CustomerResponse,
      mdmId: '',
      productOffersInCart: [] as ProductOfferingsResponse[],
      holidays: [],
      billChannels: [],
    };
  }
  const shoppingBasket = parseBasketJsonCoCo(getShoppingBasketFromLocalStorageCoCo(scope));
  const offerIds = shoppingBasket?.productOfferings?.map(item => item.id) || [];
  const [contacts, customerInfo, productOffersInCart, holidaysResponse, billChannelsResponse] = await Promise.all([
    // TODO : fetchContacts call will be removed later for coco
    fetchContacts({ offset: 0 }, { mdmId }),
    fetchCustomer(),
    offerIds.length > 0 ? fetchProductOffersByOfferId(offerIds) : Promise.resolve([] as ProductOfferingsResponse[]),
    fetchHolidays(),
    fetchBillChannels(mdmId),
  ]);
  return {
    contacts,
    customerInfo,
    mdmId,
    productOffersInCart,
    holidays: holidaysResponse.dates.map(date => new Date(date)),
    billChannels: billChannelsResponse,
  };
};

export const placeProductOrderLoader = async (): Promise<PlaceProductOrderLoaderData> =>
  loadPlaceProductOrderData(ShoppingBasketCoCoScope.ADMIN);

export const placeEmployeeProductOrderLoader = async (): Promise<PlaceProductOrderLoaderData> =>
  loadPlaceProductOrderData(ShoppingBasketCoCoScope.EMPLOYEE);

export const fetchProductPageLoader = async (args: LoaderFunctionArgs): Promise<PageResponse> => {
  const url = new URL(args.request.url);
  url.pathname = `/${args.params[deviceName]}`;
  return fetchPublicPageLoader({ ...args, request: new Request(url, args.request) });
};

export const fetchNavigationGroupsLoader = async (args: LoaderFunctionArgs): Promise<NavigationResponse> => {
  return fetchNavigationLoader(args);
};

export const fetchNavigationMenuIfUnauthenticatedLoader = async (
  args: LoaderFunctionArgs
): Promise<NavigationResponse> => {
  const response = await fetchNavigationLoader(args);
  if (getActiveAccountMasterId()) {
    return { ...response, menu: [] };
  }
  return response;
};

export const invitationLandingLoader = async ({
  request,
}: LoaderFunctionArgs): Promise<InvitationLanding | undefined> => {
  const url = new URL(request.url);
  const invitationId = url.searchParams.get('invitationid');

  if (!invitationId) {
    throw new Error('Missing invitationid parameter');
  }

  return fetchInvitation(invitationId);
};

const buildProposalsQuery = (request: Request): SortableLimitableQuery & { filter: string } => {
  const { params, offset, itemsPerPage, sort, order } = getSearchParams(request);

  let sortString = sort && order && order === 'desc' ? `-${sort}` : sort;
  if (!sortString || sortString === '') {
    sortString = '-creationDate';
  }

  const quoteStatus = params.get('status');
  // Convert pipe-separated statuses from URL (approved|rejected) to comma-separated for filter (approved,rejected)
  const statusForFilter = quoteStatus ? quoteStatus.replace(/\|/g, ',') : null;
  const filter: string = statusForFilter
    ? `status==${statusForFilter}`
    : `status==${QuoteStatusType.ACCEPTED},${QuoteStatusType.REJECTED},${QuoteStatusType.APPROVED}`;

  return {
    filter,
    limit: itemsPerPage,
    offset: Number(offset ?? 0),
    sort: sortString,
  };
};

export const serviceProposalsLoader = ({ request }: LoaderFunctionArgs) => {
  const query = buildProposalsQuery(request);
  return fetchProposals(query);
};

export const fetchProposalPageLoader = async ({ params }: LoaderFunctionArgs): Promise<ProposalResponse> => {
  const proposalId = params.proposalId;
  if (!proposalId) {
    throw new Error('Missing proposalId parameter');
  }
  return fetchProposalByProposalId(proposalId);
};

export const updateProposalRequestById = async (
  proposalId: string,
  request: UpdateProposalRequest
): Promise<UpdateProposalResponse> => {
  if (!proposalId) {
    throw new Error('Missing proposalId parameter');
  }
  return updateProposalByProposalId(proposalId, request);
};

export const fetchProductOfferByOfferIdPageLoader = async ({
  params,
}: LoaderFunctionArgs): Promise<ProductOfferingsResponse> => {
  const offerId = params.offerId;
  if (!offerId) {
    throw new Error('Missing offerId parameter');
  }
  return fetchProductOfferByOfferId(offerId);
};
